/**
 * Shared game constants — the single source of truth used by both client and server.
 */
/** Colyseus state patch / network tick rate (Hz). 20Hz is the Colyseus default. */
export const TICK_RATE = 20;
/** Player walk speed in pixels per second. */
export const MOVE_SPEED = 80;
// --- BMX (rideable bike) ------------------------------------------------------
// GTA-style momentum model, adapted to the 2D server space: pedaling ACCELERATES a velocity
// vector, releasing coasts against drag, steering ROTATES the velocity (carving arcs whose
// radius grows with speed), Shift sprints the pedals (stamina), Space bunny-hops.
export const BMX_ID = "bmx";
export const BMX_SPEED = 210; // top speed pedaling (px/s) — vs 80 walk / 136 sprint-run
export const BMX_SPRINT_MULT = 1.35; // Shift: sprint pedaling on top of BMX_SPEED
export const BMX_ACCEL = 260; // pedal acceleration (px/s²)
export const BMX_BRAKE = 460; // deceleration when the input opposes the motion (px/s²)
export const BMX_DRAG = 0.8; // coasting drag (exponential, per second)
export const BMX_TURN_MAX = 5.0; // steering rate at crawl speed (rad/s) …
export const BMX_TURN_MIN = 2.0; // … tightening to this at top speed (wide carve arcs)
export const BMX_SIT_Y = 0.34; // rider sprite lift onto the saddle (world units)
export const BMX_MOUNT_RANGE = 72; // px — how close you must stand to hop on
/**
 * Owner of the WINSTON'S tavern building (character "Ghost"). Only this character may
 * lock/unlock the tavern (press L near the door); everyone reads the shared lock state.
 * Hardcoded for the single tavern in v1; a per-building owner field would generalize this.
 */
export const TAVERN_OWNER_ID = "6a2f9e33670a0b36cee94cf7";
// ── Property / building ownership ───────────────────────────────────────────
/**
 * The ownable buildings (each enterable interior + the spawn-island tavern). A property belongs to a
 * character; the owner may lock/unlock it and hire employees. Seeded to Ghost (admin) initially.
 * `id` matches the building's zone/door key used by the scene + lock state.
 */
export const OWNABLE_BUILDINGS = [
    { id: "tavern", name: "Winston's (Island)", price: 120000 },
    { id: "citytavern", name: "Winston's", price: 100000 },
    { id: "threads", name: "Threads", price: 60000 },
    { id: "casino", name: "Casino", price: 250000 },
    { id: "bank", name: "Bank", price: 180000 },
    { id: "food", name: "Watabaga", price: 70000 },
    { id: "ghosts", name: "Ghost Lab", price: 50000 },
    { id: "furni", name: "Okka Furniture", price: 90000 },
    { id: "tackle", name: "Old Timer's Baits", price: 30000 },
    { id: "smith", name: "Blacksmith", price: 80000 },
    // The 4 apartment HOMES in the city tenement block (floors 2 + 3, two units each). Houses, not
    // businesses: no staff/jobs — instead the owner hands out door KEYS (Building.keyholders).
    { id: "apt2a", name: "Apartment 2A", price: 40000 },
    { id: "apt2b", name: "Apartment 2B", price: 40000 },
    { id: "apt3a", name: "Apartment 3A", price: 40000 },
    { id: "apt3b", name: "Apartment 3B", price: 40000 },
    // The 4 homes of the ANNEX — the second tenement block, right beside the first. Same layout
    // (floors 2 + 3, two units each). Seeded to Ghost like every building.
    { id: "anx2a", name: "Annex 2A", price: 40000 },
    { id: "anx2b", name: "Annex 2B", price: 40000 },
    { id: "anx3a", name: "Annex 3A", price: 40000 },
    { id: "anx3b", name: "Annex 3B", price: 40000 },
];
export const BUILDING_IDS = OWNABLE_BUILDINGS.map((b) => b.id);
export function isBuildingId(id) {
    return BUILDING_IDS.includes(id);
}
export function buildingName(id) { return OWNABLE_BUILDINGS.find((b) => b.id === id)?.name ?? id; }
/** Market price to buy an unowned building. */
export function buildingPrice(id) { return OWNABLE_BUILDINGS.find((b) => b.id === id)?.price ?? 0; }
/** Selling a building back to the market refunds this fraction of its price. */
export const SELL_REFUND_FRAC = 0.5;
// ── Houses (apartments) ──────────────────────────────────────────────────────
/** House buildings (player HOMES — the apartment units). Owned/listed/sold like any building but
 *  counted against the separate house limit, never hire staff, and support door KEYS instead. */
export const HOUSE_IDS = ["apt2a", "apt2b", "apt3a", "apt3b", "anx2a", "anx2b", "anx3a", "anx3b"];
export function isHouseBuilding(id) { return HOUSE_IDS.includes(id); }
/** Houses a character may own (separate from the business building limit). */
export const DEFAULT_HOUSE_LIMIT = 1;
/** Max door keys a house owner may hand out. */
export const MAX_KEYHOLDERS_PER_HOUSE = 10;
// ── Building auctions ────────────────────────────────────────────────────────
/** An admin may put an owned building up for auction for 1–48 hours. */
export const AUCTION_MIN_HOURS = 1;
export const AUCTION_MAX_HOURS = 48;
// ── Jobs (Watabaga staff shifts) ────────────────────────────────────────────
/** Base wage paid to a clocked-in worker every SALARY_INTERVAL_MS, straight to carried cash. */
export const WATABAGA_SALARY = 500;
export const SALARY_INTERVAL_MS = 1200000; // 20 minutes
/** The DEFAULT per-item COST of a Watabaga food task — the wage paid to the worker who completes it (out
 *  of the building register) and the floor for both its price and the owner-adjustable cost. The OWNER can
 *  raise/lower the actual cost per building (Building.costs). Items not here aren't "food tasks". */
export const FOOD_COST = {
    hog_burger: 30,
    fries: 15,
    water: 10,
    soda: 10,
};
export function foodCost(itemId) { return FOOD_COST[itemId] ?? 0; }
/** A city's typical price for each menu item — the benchmark customers judge a store's prices against
 *  (hardcoded per city; v1 has one city). Pricing at/below it draws customers fastest. */
export const CITY_AVG_PRICE = {
    water: 30,
    soda: 30,
    hog_burger: 90,
    fries: 45,
};
/** Max units of a single item the store chest will hold by default (the owner can lower this per item
 *  via Building.maxes). Caps how many of one good can be banked. */
export const FRIDGE_ITEM_CAP = 100;
export const DEFAULT_ITEM_MAX = FRIDGE_ITEM_CAP;
/** Auto-customer cadence: a base interval scaled by how the menu prices compare to the CITY AVERAGE (the
 *  worst price/avg ratio across the menu), ± jitter. At ≤ the city average it's the fast base; at ≥ 2× the
 *  average it's the slow base; linear between. */
export const AUTO_CUSTOMER_FAST_MS = 300000; // 5 min — priced at/under the city average, customers flock
export const AUTO_CUSTOMER_SLOW_MS = 600000; // 10 min — gouging (≥2× average), customers stay away
export const AUTO_CUSTOMER_JITTER_MS = 60000; // ± 1 minute
export const AUTO_CUSTOMER_RATIO_FAST = 1; // price ≤ city average ⇒ fast base
export const AUTO_CUSTOMER_RATIO_SLOW = 2; // price ≥ 2× city average ⇒ slow base
/** The base (pre-jitter) auto-customer interval for a given highest price/city-average ratio. */
export function autoCustomerBaseMs(maxRatio) {
    const t = Math.max(0, Math.min(1, (maxRatio - AUTO_CUSTOMER_RATIO_FAST) / (AUTO_CUSTOMER_RATIO_SLOW - AUTO_CUSTOMER_RATIO_FAST)));
    return AUTO_CUSTOMER_FAST_MS + t * (AUTO_CUSTOMER_SLOW_MS - AUTO_CUSTOMER_FAST_MS);
}
/** The Watabaga fridge (building store) holds this many kilograms of items (every item = 1kg). */
export const FRIDGE_CAPACITY_KG = 300;
/** Only buildings with a fridge / staff jobs. (v1: just Watabaga.) */
export const FRIDGE_BUILDING_ID = "food";
/** The red work uniform forced onto a clocked-in worker (red tee + red jeans). Garment colour 6 = red,
 *  top style 1 = tee, bottom style 1 = jeans. The worker's real top/bottom are restored on clock-out. */
export const WORK_UNIFORM = { top: 1, topColor: 6, bottom: 1, bottomColor: 6, shoes: 1, shoesColor: 6 };
/** The THREADS work uniform: the full purple Kapon Sporty set (top style 4 / bottom style 3 / shoes
 *  style 1, colour 7 = purple) — matches the purple Kapon set in ITEM_EQUIP. Locked on while clocked in. */
export const THREADS_UNIFORM = { top: 4, topColor: 7, bottom: 3, bottomColor: 7, shoes: 1, shoesColor: 7 };
/** The work uniform forced onto a clocked-in worker at a given business (restored on clock-out). */
export function workUniform(bid) {
    return bid === "threads" ? THREADS_UNIFORM : WORK_UNIFORM;
}
/** Buildings a character may own by default; raised per-character later (Character.buildingLimit). */
export const DEFAULT_BUILDING_LIMIT = 2;
/** Ghost (the admin character) building allowance. */
export const GHOST_BUILDING_LIMIT = 100;
/** Max employees a single building may hire. */
export const MAX_EMPLOYEES_PER_BUILDING = 10;
/** Tile size of the TimeFantasy pack, in pixels. */
export const TILE_SIZE = 16;
/** Colyseus room registration name. */
export const ROOM_NAME = "game_room";
/** How often the server persists player positions, in milliseconds. */
export const POSITION_SAVE_INTERVAL_MS = 5000;
/** Seconds the server holds a seat for reconnection after an unexpected drop. */
export const RECONNECT_GRACE_SECONDS = 30;
/** Custom WebSocket close code: the account signed in from another session. */
export const KICK_DUPLICATE_SESSION = 4001;
/**
 * The character spritesheet used in v1 and the selectable sprite indices.
 * `chara4.png` is an RPG Maker MV sheet of 8 heroes (a 12x8 frame grid, 26x36 px frames).
 */
export const SPRITE_SHEET = "chara4";
export const SPRITE_FRAME_WIDTH = 26;
export const SPRITE_FRAME_HEIGHT = 36;
export const SPRITE_IDS = [0, 1, 2, 3, 4, 5, 6, 7];
export function isValidSpriteId(value) {
    return typeof value === "number" && SPRITE_IDS.includes(value);
}
/**
 * Layered "paperdoll" character art (the "Elements" pack in
 * apps/client/public/assets/character/parts/). Every part sheet is a uniform
 * 1104x192 grid = 24 columns x 4 rows of 46x48 frames, pre-aligned so layers
 * overlay directly. Rows are facing directions in RPG-Maker order (down/left/
 * right/up); the idle column + walk-cycle columns are pinned empirically in the
 * client render (Phase 3) and live in the sprite code, not here.
 */
export const PART_FRAME_W = 46;
export const PART_FRAME_H = 48;
export const PART_COLS = 24;
export const PART_ROWS = 4;
/**
 * The pack's skin colour ramp (light -> mid -> shadow -> dark outline), exactly as it
 * appears in the part PNGs. Skin pixels exist on heads AND on clothing layers (hands,
 * arms, bare legs under skirts), so skin tone is applied as a render-time palette
 * remap of these four colours on every layer — no per-tone asset files.
 */
export const SKIN_RAMP = [
    [244, 210, 156],
    [219, 164, 99],
    [187, 117, 71],
    [115, 23, 45],
];
/**
 * Selectable skin tones. Index 0 is the pack default (identity remap); each entry is
 * a replacement ramp for SKIN_RAMP, hand-picked to keep the warm hue + step contrast
 * of the original art.
 */
export const SKIN_TONES = [
    SKIN_RAMP, // 0 default
    [[247, 227, 198], [227, 189, 150], [192, 140, 106], [122, 36, 56]], // 1 pale
    [[232, 182, 130], [201, 138, 82], [160, 95, 56], [94, 20, 36]], // 2 tan
    [[201, 138, 82], [160, 95, 56], [122, 67, 38], [71, 16, 24]], // 3 bronze
    [[160, 106, 66], [125, 77, 44], [92, 53, 29], [51, 11, 18]], // 4 brown
    [[125, 81, 58], [94, 58, 40], [66, 40, 26], [31, 7, 9]], // 5 deep
];
/** Each account may own up to this many characters (3 selection slots). */
export const MAX_CHARACTERS_PER_ACCOUNT = 3;
/** Maps available in v1. */
export const MAPS = {
    town: "town",
};
export const DEFAULT_MAP = MAPS.town;
/**
 * Default/fallback spawn point in pixel coordinates — matches the `spawn` object
 * authored in the Tiled town map (tile 20,15 -> 320,240, the centre of the sand
 * plaza). New characters are created here; it is also the safety net when a
 * persisted position is missing or invalid.
 */
export const SPAWN = { x: 320, y: 240 };
/** Display-name validation bounds. */
export const NAME_MIN = 3;
export const NAME_MAX = 20;
/**
 * Item registry — the canonical set of pickable item types. Mirrors the
 * `SPRITE_IDS` validator pattern so both client and server agree on what's valid.
 * v1 items are all equippable garments.
 */
export const ITEM_IDS = [
    "black_shirt", "blue_jeans", "black_cap", "black_shoes", "ghost_tee", "work_pants",
    // Threads shop stock — basic tees ($500) and the Kapon Sporty jumpsuit pieces ($100k each).
    "white_tee", "red_tee", "green_tee", "blue_tee", "purple_tee", "circle_shirt", "pickaxe_tee", "bomber_jacket",
    "kapon_sweatshirt", "kapon_sweatpants", "purple_sweatshirt", "purple_sweatpants",
    // Colour baseball caps (recolourable hat style 1) — craftable at the Threads sewing machine.
    "white_cap", "red_cap", "green_cap", "blue_cap", "purple_cap",
    "ghost_scroll", "pickaxe", "grass_cutter",
    "ore_gold", "ore_silver", "ore_iron", "ore_copper", "ore_stone",
    // Weapons + offhand gear — held in a hand slot; only drawn on the character in COMBAT mode.
    "sword",
    "round_shield",
    // Skinning — the tool + the hides it yields.
    "skinning_knife",
    "hog_hide",
    // Crafted materials — profession outputs (Leather Smith / Black Smith).
    "leather_low",
    "copper_bar", "iron_bar", "silver_bar", "gold_bar",
    // Crafted LEATHER ARMOR (Leather Smith Lv5) — top + pants, each in 3 stat-roll variants
    // (str/dex/agi). Equippable brown gear granting +5 armor + 1 to the rolled stat.
    "leather_top_str", "leather_top_dex", "leather_top_agi",
    "leather_pants_str", "leather_pants_dex", "leather_pants_agi",
    // ARMORED leather (Leather Smith Lv8) — upgraded from a normal piece. Same base stats PLUS 3 random
    // bonus points stored PER-INSTANCE (InventorySlot.mods). Non-stackable; each is unique.
    "armored_leather_top_str", "armored_leather_top_dex", "armored_leather_top_agi",
    "armored_leather_pants_str", "armored_leather_pants_dex", "armored_leather_pants_agi",
    // Consumable — smoke it to stand-and-smoke for 20s while healing over time.
    "blunt",
    // Food-store consumables — eat/drink to restore hunger/thirst.
    "fries", "water", "soda",
    // Watabaga cooking: dishes cooked at the pot + their raw ingredients.
    "hog_burger", "potato",
    // Hog drops + loot-chest finds.
    "hog_meat", "hog_charm", "hog_tusk", "book_running",
    // Goblin + slime drops.
    "goblin_ear", "book_goblin_tech", "magic_dust",
    // Fishing — the rod tool + the 6 fish it yields (rarity white → yellow).
    "fishing_rod", "mudfish", "pondfish", "bog_carp", "moss_perch", "ghost_koi", "sun_carp", "worms",
    // Furniture (Okka Furniture stock) — placed inside an owned property via Use (see FURNITURE_SHOP).
    // The furnace is the Black Smith's smelting station; the anvil awaits forging recipes.
    "bed", "safebox", "furnace", "anvil",
    // Rideable BMX bike — Use drops/stows it, E hops on (see BMX_* physics constants).
    "bmx",
    // MASK-slot garments (worn over the face, between hair and hat).
    "balaclava",
    "fishing_hat",
    // Weed growing (Black Market): the seed you plant in the Kapon wilds, the watering pot that
    // keeps it alive, the buds a mature plant yields, and the papers that roll buds into blunts.
    "weed_seed", "watering_pot", "weed_bud", "rolling_paper",
];
/** Items the player can "Use" from the inventory (consumed or triggering an action). */
export const GHOST_SCROLL_ID = "ghost_scroll";
/** The mining tool. Held in the inventory (persisted); carrying one lets the player mine. */
export const PICKAXE_ID = "pickaxe";
/** The grass-cutting tool. Held in the inventory (persisted); carrying one lets the player cut grass. */
export const GRASS_CUTTER_ID = "grass_cutter";
/** The fishing tool. Carrying one in the bag lets the player fish at water's edge (press E). */
export const FISHING_ROD_ID = "fishing_rod";
/** The bait item loaded into a rod (sold at Old Timer's Baits) + how many one rod holds. */
export const BAIT_ITEM_ID = "worms";
export const ROD_BAIT_MAX = 99;
/** Weight (kg) one SLOT of an item contributes: worms are near-weightless — a whole bait stack
 *  weighs 1kg — while everything else weighs ITEM_WEIGHT_KG per unit. */
export function slotWeightKg(itemId, quantity) {
    return itemId === BAIT_ITEM_ID ? 1 : quantity;
}
/** Total carried weight (kg) across occupied slots. */
export function carryWeightKg(slots) {
    return slots.reduce((n, s) => n + slotWeightKg(s.itemId, s.quantity), 0);
}
/** Validate/coerce a persisted rod `bait` count: integer 0..ROD_BAIT_MAX; undefined for non-rods. */
export function sanitizeRodBait(itemId, raw) {
    if (itemId !== FISHING_ROD_ID)
        return undefined;
    const n = Number(raw);
    if (!Number.isFinite(n))
        return undefined;
    return Math.max(0, Math.min(ROD_BAIT_MAX, Math.floor(n)));
}
/** The skinning tool. Carrying one (bought from the skinner) enables the skinning skill. */
export const SKINNING_KNIFE_ID = "skinning_knife";
/** The hide yielded by skinning a hog (a white-tier beast). */
export const HOG_HIDE_ID = "hog_hide";
/** The Blunt — a usable consumable. Smoking it plays a standing-smoke idle for BLUNT_DURATION_MS and
 *  heals BLUNT_HEAL_AMOUNT every BLUNT_HEAL_INTERVAL_MS for the duration. */
export const BLUNT_ID = "blunt";
export const BLUNT_DURATION_MS = 20000; // smoke (and heal) for 20 seconds
export const BLUNT_HEAL_INTERVAL_MS = 5000; // heal tick cadence — every 5 seconds
export const BLUNT_HEAL_AMOUNT = 15; // HP restored per heal tick (up to maxHealth)
/** The Hog Charm — right-click to summon a mini pet hog that follows you across scenes. Reusable on a
 *  cooldown (not consumed). The pet never attacks; other players can kill it in the wild. */
export const HOG_CHARM_ID = "hog_charm";
export const HOG_CHARM_COOLDOWN_MS = 60000; // 1 minute between summons
export const PET_HOG_HEALTH = 30;
export const PET_FOLLOW_GAP = 26; // server px: how far behind the owner the pet settles
export const PET_TELEPORT_DIST = 260; // server px: owner jump beyond this snaps the pet (scene change)
export const PET_SPEED = 110; // server px/sec while following (a touch faster than the player)
export const PET_TICK_MS = 100; // pet follow-AI tick cadence
/** The basic melee weapon. Equip into a hand slot; only rendered on the character in combat mode. */
export const SWORD_ID = "sword";
/** The offhand round shield. Equip into the SECOND hand; blocking with it negates attacks whose
 *  damage is ≤ its armor. */
export const SHIELD_ID = "round_shield";
/**
 * Weed growing. A seed (Black Market) is planted on open Kapon ground via Use. The plant grows
 * through WEED_STAGES stages of WEED_STAGE_MS each (fully grown in 20 min) — but each stage only
 * runs while WATERED: a freshly planted/advanced stage starts THIRSTY, and one watering
 * (POT_WATER_PER_USE_ML from a carried pot) fuels exactly one stage. A stage left thirsty for
 * WEED_DRY_ROT_MS rots the plant. Anyone may inspect (E) / water / harvest a plant — the wilds
 * are lawless — and there is no cap on plants per player; only the spacing ring gates planting.
 */
export const WEED_SEED_ID = "weed_seed";
export const WATERING_POT_ID = "watering_pot";
export const WEED_BUD_ID = "weed_bud";
/** The watering pot's tank (ml): fills to 500 at a water body; each watering pours 100. */
export const POT_WATER_MAX_ML = 500;
export const POT_WATER_PER_USE_ML = 100;
export const WEED_STAGES = 4;
/** Per-stage WATERED grow time — 4 × 5 min = fully grown in 20 minutes. */
export const WEED_STAGE_MS = 5 * 60_000;
/** A thirsty stage rots (plant dies) after this long without water. */
export const WEED_DRY_ROT_MS = 5 * 60_000;
/** Buds harvested from a mature plant (uniform roll, inclusive). */
export const WEED_YIELD_MIN = 6;
export const WEED_YIELD_MAX = 12;
/** How close (server px) a player must stand to inspect/water/harvest a plant. */
export const WEED_INTERACT_RANGE = 56;
/** Minimum spacing (server px) between two plants (~0.7 world units). Planting inside this ring
 *  is rejected with a "too close" notice. Mature sprites may partially overlap at this density —
 *  tune here if farms get too crowded. */
export const WEED_PLANT_SPACING = 22;
/** A rotten plant crumbles away on its own after this long (keeps the meadow clean). */
export const WEED_ROTTEN_TTL_MS = 10 * 60_000;
/** Rolling: right-click a Rolling Paper with enough buds in the bag → a ROLL_BLUNT_MS channel
 *  (progress bar over the player; moving cancels) that turns 1 paper + WEED_BUDS_PER_BLUNT buds
 *  into 1 Blunt (the existing smokable heal item). */
export const ROLLING_PAPER_ID = "rolling_paper";
export const WEED_BUDS_PER_BLUNT = 3;
export const ROLL_BLUNT_MS = 5000;
/** Validate/coerce a persisted pot `water` level (ml): integer 0..POT_WATER_MAX_ML; undefined for
 *  non-pots. Mirrors sanitizeRodBait — the pot is the second item with a per-instance tank. */
export function sanitizePotWater(itemId, raw) {
    if (itemId !== WATERING_POT_ID)
        return undefined;
    const n = Number(raw);
    if (!Number.isFinite(n))
        return undefined;
    return Math.max(0, Math.min(POT_WATER_MAX_ML, Math.floor(n)));
}
/** Distance from a point to the line SEGMENT (ax,az)→(bx,bz) — shared by the plant-spot check. */
function segDist2D(px, pz, ax, az, bx, bz) {
    const vx = bx - ax, vz = bz - az;
    const wx = px - ax, wz = pz - az;
    const len2 = vx * vx + vz * vz || 1;
    const t = Math.max(0, Math.min(1, (wx * vx + wz * vz) / len2));
    return Math.hypot(px - (ax + t * vx), pz - (az + t * vz));
}
/** Whether (x,y) — SERVER px — is plantable Kapon ground: inside the field with a margin, off the
 *  lake, off the camp, and off the road network (trunk + both forks). Shared so the client can
 *  toast the right reason and the server can authoritatively reject. */
export function weedSpotOk(x, y) {
    const T = KAPON_TERRAIN;
    const wx = x / 32, wz = y / 32; // server px → world units (32 px per unit)
    if (wx < T.cx - T.hw + 2 || wx > T.cx + T.hw - 2)
        return false;
    if (wz < T.northBound + 2 || wz > T.cz + T.hd - 3)
        return false;
    if (Math.hypot(wx - T.lake.x, wz - T.lake.z) <= T.lake.r + 1.5)
        return false;
    if (Math.hypot(wx - T.camp.x, wz - T.camp.z) <= T.camp.r)
        return false;
    const gateZ = T.cz + T.hd;
    const road = Math.min(segDist2D(wx, wz, T.cx, gateZ, T.cx, T.forkZ), segDist2D(wx, wz, T.cx, T.forkZ, T.cave.x, T.cave.z), segDist2D(wx, wz, T.cx, T.forkZ, T.forest.x, T.forest.z));
    const half = wz > T.narrowZ ? T.entryHalf : T.pathHalf;
    return road > half + 0.8;
}
/** Item ids that are weapons (go into the PRIMARY hand, deal damage). */
export const WEAPON_IDS = ["sword"];
export function isWeapon(id) {
    return WEAPON_IDS.includes(id);
}
/** Item ids that are offhand shields (go into the SECOND hand, provide block armor). */
export const SHIELD_IDS = ["round_shield"];
export function isShield(id) {
    return SHIELD_IDS.includes(id);
}
/** Either a weapon or a shield — anything that occupies a hand slot. */
export function isHandItem(id) {
    return isWeapon(id) || isShield(id);
}
/** Per-weapon melee damage (HP removed on an unblocked hit). */
export const WEAPON_DAMAGE = { sword: 20 };
export function weaponDamage(itemId) {
    return WEAPON_DAMAGE[itemId] ?? 0;
}
/** Per-shield BLOCK score. A raised block fully negates (and staggers the attacker) when the
 *  incoming damage is BELOW this; a bigger hit pierces, dealing only the excess (damage − block). */
export const SHIELD_ARMOR = { round_shield: 40 };
export function shieldArmor(itemId) {
    return SHIELD_ARMOR[itemId] ?? 0;
}
/** How long an attacker is stunned (no actions, no movement) after their hit is fully blocked. */
export const BLOCK_STUN_MS = 1000;
// --- combat tuning ----------------------------------------------------------
export const ATTACK_RANGE = 70; // server px: close melee reach — must be near the SELECTED target
export const ATTACK_COOLDOWN_MS = 1000; // min gap between your attacks
export const BLOCK_DURATION_MS = 2000; // a block stance holds for this long after pressing E
export const BLOCK_COOLDOWN_MS = 5000; // min gap between blocks (measured from when the block began)
export const DEATH_DURATION_MS = 60000; // downed (laying) time before respawn — 1 minute
/**
 * Passive health regen (not while downed). Each player heals `healthRegenAmount` HP every
 * `healthRegenIntervalMs`, BUT only while both hunger AND thirst are ≥ HEALTH_REGEN_MIN_VITALS.
 *  - amount  = 2 + strength/2 + knowledge/2   (base-stat LEVELS)
 *  - cadence = 6 × (1 − dex/200) seconds      (higher dex → faster), floored at 1s
 * The server runs a fast tick (REGEN_TICK_MS) and accumulates per character.
 */
export const REGEN_TICK_MS = 1000;
export const HEALTH_REGEN_MIN_VITALS = 50;
export function healthRegenAmount(strengthLevel, knowledgeLevel) {
    return Math.round(2 + knowledgeLevel * 1.5 + strengthLevel * 0.5); // Knowledge-led; scales with the bigger HP pools
}
export function healthRegenIntervalMs(dexLevel) {
    return Math.max(1000, Math.round(6000 * (1 - dexLevel / 200)));
}
// --- salon -----------------------------------------------------------------
/** Cost (cash) to change your hair style/colour at the salon. */
export const HAIR_CHANGE_PRICE = 500;
// --- hog attack-NPC tuning --------------------------------------------------
/** Compass facings, in the player billboard's row order (row 0 = Down/south, clockwise). The
 *  client maps a hog's `dir` → this index → a heading angle to pick its directional sprite row. */
export const HOG_DIRS = ["south", "south-east", "east", "north-east", "north", "north-west", "west", "south-west"];
export const HOG_MAX = 6; // most live hogs at once in the wilds
export const HOG_SPAWN_INTERVAL_MS = 25000; // a new hog trickles in this often, up to the cap
export const HOG_TICK_MS = 100; // server AI tick (10Hz); positions sync at the 20Hz patch rate
export const HOG_HEALTH = 220; // hog hit points — a real fight for a low-level player
export const HOG_DAMAGE = 35; // raw damage a hog lunge deals (before the player's armor)
export const HOG_ARMOR = 100; // hog armor — ~33% damage reduction under the % model (armor/(armor+200))
export const HOG_WALK_SPEED = 42; // server px/sec while wandering
export const HOG_RUN_SPEED = 122; // server px/sec while charging a target (player run = 136)
export const HOG_AGGRO_RANGE = 220; // server px: detection range — acquire a target this close
export const HOG_DEAGGRO_RANGE = HOG_AGGRO_RANGE * 4; // chase/leash range = 4× detection: a sprinting player can't shake aggro by pulling slightly ahead; only escapes by getting FAR
export const HOG_CHARGE_RANGE = 60; // server px: must be RIGHT up on the target to begin the wind-up
export const HOG_ATTACK_RANGE = 52; // server px: lunge only connects if the target is this close when it lands
export const HOG_CHARGE_MS = 2000; // wind-up: steps back for ~2s (the "charging" tell)
export const HOG_ATTACK_MS = 500; // forward lunge duration; damage lands mid-lunge
export const HOG_RECOVER_MS = 1000; // stands still ~1s after a lunge before resuming
export const HOG_ALERT_MS = 1200; // how long the "!" popup shows after acquiring a target
export const HOG_CORPSE_MS = 300000; // a slain hog's body lingers 5 minutes, then despawns
// --- wild GOBLIN (combat level 10): tougher than a hog; WALKS into melee then an overhead smash ----
// The hit lands MID-SWING (GOBLIN_HIT_MS into the GOBLIN_ATTACK_MS window) — not at the end — so a
// player who sees the "attack" state sync has ~GOBLIN_HIT_MS to raise a block.
export const GOBLIN_MAX = 4;
export const GOBLIN_SPAWN_INTERVAL_MS = 30000;
export const GOBLIN_HEALTH = 440;
export const GOBLIN_DAMAGE = 60;
export const GOBLIN_ARMOR = 130;
export const GOBLIN_WALK_SPEED = 55; // walks (no charge-run) into range
export const GOBLIN_AGGRO_RANGE = 240;
export const GOBLIN_DEAGGRO_RANGE = GOBLIN_AGGRO_RANGE * 4; // sticky chase: keeps coming until the player is genuinely far away
export const GOBLIN_ATTACK_RANGE = 52; // begins the overhead smash within this; hit connects within this (slightly longer reach)
export const GOBLIN_ATTACK_MS = 1000; // full attack: arm raises (wind-up) then swings down
export const GOBLIN_HIT_MS = 600; // damage applied at this point INTO the attack = mid-swing (block window)
export const GOBLIN_RECOVER_MS = 800; // stands a beat after the swing
export const GOBLIN_CORPSE_MS = 300000;
// --- wild SLIME (combat level 15): tanky; hops close then RANDOMLY a forward slam OR an AoE splash --
export const SLIME_MAX = 4;
export const SLIME_SPAWN_INTERVAL_MS = 30000;
export const SLIME_HEALTH = 650;
export const SLIME_ARMOR = 150;
export const SLIME_FWD_DAMAGE = 75; // single-target forward lunge slam
export const SLIME_SPLASH_DAMAGE = 60; // AoE splash: hits EVERY combatant within SLIME_SPLASH_RADIUS
export const SLIME_SPLASH_RADIUS = 90; // server px around the slime hit by the splash burst
export const SLIME_WALK_SPEED = 30; // slow / kiteable
export const SLIME_AGGRO_RANGE = 200;
export const SLIME_DEAGGRO_RANGE = SLIME_AGGRO_RANGE * 4; // sticky chase: keeps coming until the player is genuinely far away
export const SLIME_ATTACK_RANGE = 54; // gets this close, then picks an attack (50/50) (slightly longer reach)
export const SLIME_FWD_MS = 900; // forward lunge attack duration
export const SLIME_FWD_HIT_MS = 550; // contact moment of the forward lunge
export const SLIME_SPLASH_MS = 1500; // splash: green charge-up glow then burst
export const SLIME_SPLASH_HIT_MS = 950; // burst moment (AoE applied here, after the charge-up tell)
export const SLIME_RECOVER_MS = 900;
export const SLIME_CORPSE_MS = 180000;
/** Kapon sculpted-terrain heightfield params (WORLD units; cx/cz match the Kapon zone center).
 *  The clearing (combat + ore + spawn) and the N–S path stay flat; hills roll up elsewhere; a river
 *  band along the north bounds movement, with a small lake in the woods. See docs/plans Kapon plan. */
export const KAPON_TERRAIN = {
    // Expanded wilds: footprint grown to half-extents 60 and shifted north (cz −90→−101) so the SOUTH
    // gate line (cz+hd = −41) stays put against the city while the meadow stretches further north.
    cx: 70, cz: -101, hw: 60, hd: 60,
    // Octopath-style: a large, mostly FLAT meadow with only very subtle undulation toward the edges.
    clearR: 40, clearRamp: 14, // bigger flat clearing (combat + ore + mob spawn) + a gentle ramp out
    pathHalf: 4, // flat road half-width through the open area + forks
    entryHalf: 2.2, // NARROW entry-corridor half-width (tree-lined approach from the gate)
    narrowZ: -74, // south of this z = the narrow tree-lined entry corridor
    hillAmp: 0.8, // VERY gentle rolls (was a tall 3.6 open-world hill height)
    // A guided trek NORTH from the south gate: narrow entry → open meadow (lake WEST, hogs EAST) → a
    // goblin outpost on the road → the road FORKS (east → cave, west → forest path).
    lake: { x: 40, z: -86, r: 13 }, // WEST of the road, in the open area
    camp: { x: 70, z: -118, r: 12 }, // goblin outpost centre (on the road; kept flat)
    forkZ: -134, // the road splits here, just past the camp
    cave: { x: 106, z: -150 }, // EAST fork → cave entrance
    forest: { x: 34, z: -150 }, // WEST fork → forest path
    northBound: -156, // deepest walkable z (past the forks)
};
// --- loot chest (dropped by a slain hog) ---
export const HOG_DROP_CHANCE = 0.20; // 20% chance a slain hog drops a loot chest
export const CHEST_TTL_MS = 120000; // an untouched chest despawns after 2 minutes
export const CHEST_RANGE = 110; // server px: how close to open / take from a chest
/** Loot pool a chest rolls 1-2 items from (weighted by listing them more than once). */
export const CHEST_LOOT_POOL = [
    "ore_stone", "ore_iron", "ore_iron", "ore_silver", "ore_silver", "ore_gold",
    "white_tee", "red_tee", "green_tee", "blue_tee",
    "sword", "round_shield",
];
/** Where hogs roam: a rectangle across the (enlarged) Kapon clearing (server px), well north of the city. */
// Mob spawn/wander zones, placed by the level design (server px = tile*32):
//  - HOGS roam the OPEN AREA to the EAST (right) of the road, opposite the lake.
//  - GOBLINS hold their OUTPOST around the camp on the road.
//  - SLIMES lurk deep along the WEST forest path past the fork.
export const HOG_AREA = { x0: 78 * 32, y0: -112 * 32, x1: 126 * 32, y1: -68 * 32 }; // east of the road, spread wide across the open meadow
export const GOBLIN_AREA = { x0: 56 * 32, y0: -128 * 32, x1: 84 * 32, y1: -108 * 32 }; // around the goblin outpost
export const SLIME_AREA = { x0: 16 * 32, y0: -156 * 32, x1: 54 * 32, y1: -130 * 32 }; // deep west, spread along the forest path
// --- gate guards (friendly combat NPCs that hold the Kapon side of the city gate) ----------
/** Two guards flank the south gate; they leave their post to help a player a hog is chasing, then
 *  return. Posts are derived from the Kapon terrain so they track the gate if the zone is resized. */
export const GUARD_POSTS = [
    { x: (70 - 4.5) * 32, y: (-90 + 49 - 4) * 32 }, // west of the gate, just inside (KAPON cx-4.5, cz+hd-4)
    { x: (70 + 4.5) * 32, y: (-90 + 49 - 4) * 32 }, // east of the gate
];
export const GUARD_HEALTH = 140; // tanky — guards shrug off a few hog lunges
export const GUARD_ARMOR = 22; // mitigates hog damage
export const GUARD_DAMAGE = 26; // raw damage per guard swing (before hog armor)
export const GUARD_SPEED = 116; // server px/sec while moving to engage / returning to post
export const GUARD_ATTACK_RANGE = 58; // server px: must be this close to land a swing
export const GUARD_ATTACK_COOLDOWN_MS = 900; // min ms between guard swings
export const GUARD_ATTACK_ANIM_MS = 400; // how long state="attack" shows (drives the swing anim)
export const GUARD_ASSIST_RANGE = 620; // server px: a hog threatening a player this close to the post draws the guard out
export const GUARD_LEASH = 760; // server px: beyond this from the post, the guard breaks off and returns
export const GUARD_HOME_EPS = 10; // server px: "arrived back at post" threshold
export const GUARD_RESPAWN_MS = 15000; // a downed guard re-mans the post after this
// --- affinity (PvP karma) -------------------------------------------------------------------
/** Attacking a peaceful (non-red) player costs the attacker this much affinity per landed hit. */
export const AFFINITY_ATTACK_PENALTY = 10;
/** Landing the killing blow on a peaceful player costs this much MORE (on top of the hit penalty). */
export const AFFINITY_KILL_PENALTY = 20;
/** While online and red-named, affinity recovers this much each interval, up to 0. */
export const AFFINITY_RECOVER_AMOUNT = 10;
export const AFFINITY_RECOVER_INTERVAL_MS = 600_000; // 10 minutes
/** Floor — bounds how negative affinity can go (and thus worst-case recovery time). */
export const AFFINITY_MIN = -100;
/** A player is "red-named" (skeleton marker, hunted by guards, barred from the village) when negative. */
export function isRedNamed(affinity) {
    return affinity < 0;
}
export function isValidItemId(value) {
    return typeof value === "string" && ITEM_IDS.includes(value);
}
/** Human-readable item names, shown in the inventory panel. */
export const ITEM_NAMES = {
    black_shirt: "Black Shirt",
    blue_jeans: "Blue Jeans",
    black_cap: "Black Baseball Hat",
    white_cap: "White Cap", red_cap: "Red Cap", green_cap: "Green Cap", blue_cap: "Blue Cap", purple_cap: "Purple Cap",
    black_shoes: "Black Shoes",
    ghost_tee: "Ghost Tee",
    work_pants: "Sagging Workpants",
    white_tee: "White Tee",
    red_tee: "Red Tee",
    green_tee: "Green Tee",
    blue_tee: "Blue Tee",
    purple_tee: "Purple Tee",
    circle_shirt: "Circle Tee",
    pickaxe_tee: "Pickaxe Tee",
    bomber_jacket: "DRKLRD Bomber Jacket",
    kapon_sweatshirt: "Kapon Sporty Sweatshirt",
    kapon_sweatpants: "Kapon Sporty Sweatpants",
    purple_sweatshirt: "Kapon Sporty Sweatshirt (Purple)",
    purple_sweatpants: "Kapon Sporty Sweatpants (Purple)",
    ghost_scroll: "Teleport Scroll to Ghost Land",
    pickaxe: "Pickaxe",
    grass_cutter: "Grass Cutter",
    ore_gold: "Gold Ore",
    ore_silver: "Silver Ore",
    ore_iron: "Iron Ore",
    ore_copper: "Copper Ore",
    ore_stone: "Stone",
    sword: "Iron Sword",
    round_shield: "Round Shield",
    skinning_knife: "Skinning Knife",
    hog_hide: "Hog Hide",
    leather_low: "Low Quality Leather",
    copper_bar: "Copper Bar",
    iron_bar: "Iron Bar",
    silver_bar: "Silver Bar",
    gold_bar: "Gold Bar",
    leather_top_str: "Leather Top", leather_top_dex: "Leather Top", leather_top_agi: "Leather Top",
    leather_pants_str: "Leather Pants", leather_pants_dex: "Leather Pants", leather_pants_agi: "Leather Pants",
    armored_leather_top_str: "Armored Leather Top", armored_leather_top_dex: "Armored Leather Top", armored_leather_top_agi: "Armored Leather Top",
    armored_leather_pants_str: "Armored Leather Pants", armored_leather_pants_dex: "Armored Leather Pants", armored_leather_pants_agi: "Armored Leather Pants",
    blunt: "Blunt",
    fries: "Fries",
    water: "Water",
    soda: "Soda",
    hog_burger: "Hog Burger",
    potato: "Potato",
    hog_meat: "Hog Meat",
    hog_charm: "Hog Charm",
    hog_tusk: "Hog Tusk",
    book_running: "Book of Running (Beginner)",
    goblin_ear: "Goblin Ear",
    book_goblin_tech: "Introduction to Goblin Technology",
    magic_dust: "Magic Dust",
    fishing_rod: "Basic Fishing Rod",
    worms: "Worm Bait",
    bmx: "BMX",
    balaclava: "Balaclava",
    fishing_hat: "Fishing Hat",
    mudfish: "Mudfish",
    pondfish: "Pondfish",
    moss_perch: "Moss Perch",
    sun_carp: "Sun Carp",
    bog_carp: "Bog Carp",
    ghost_koi: "Ghost Koi",
    bed: "Bed",
    safebox: "Safebox",
    furnace: "Furnace",
    anvil: "Anvil",
    weed_seed: "Weed Seed",
    watering_pot: "Watering Pot",
    weed_bud: "Weed Bud",
    rolling_paper: "Rolling Paper",
};
/** Flavour + usage text shown in the inventory hover tooltip. */
export const ITEM_DESCRIPTIONS = {
    black_shirt: "A plain black tee. Equip to wear it.",
    blue_jeans: "Sturdy blue denim. Equip to wear them.",
    black_cap: "A low-profile baseball cap. Equip to wear it.",
    white_cap: "A clean baseball cap. Equip to wear it.",
    red_cap: "A bright red baseball cap. Equip to wear it.",
    green_cap: "A leafy green baseball cap. Equip to wear it.",
    blue_cap: "A cool blue baseball cap. Equip to wear it.",
    purple_cap: "A rich purple baseball cap. Equip to wear it.",
    black_shoes: "Scuffed black sneakers. Equip to wear them.",
    ghost_tee: "Since day one of the birth of Ghost Land.",
    work_pants: "Baggy, sagging workpants. Equip to wear them.",
    white_tee: "A clean white cotton tee. Equip to wear it.",
    red_tee: "A bold red cotton tee. Equip to wear it.",
    green_tee: "A fresh green cotton tee. Equip to wear it.",
    blue_tee: "A cool blue cotton tee. Equip to wear it.",
    purple_tee: "A rich purple cotton tee. Equip to wear it.",
    circle_shirt: "A cotton tee with a tonal ring print. Equip to wear it.",
    pickaxe_tee: "A white tee with a pickaxe print on the chest — miner's pride. Equip to wear it.",
    bomber_jacket: "A black lowpoly bomber jacket with a silver zipper. Equip to wear it.",
    kapon_sweatshirt: "Kapon Sporty — a premium blue tech sweatshirt. Equip to wear it.",
    kapon_sweatpants: "Kapon Sporty — premium blue tech sweatpants. Equip to wear them.",
    purple_sweatshirt: "Kapon Sporty — a premium purple tech sweatshirt. Equip to wear it.",
    purple_sweatpants: "Kapon Sporty — premium purple tech sweatpants. Equip to wear them.",
    ghost_scroll: "A shimmering scroll inked with the road home. Use it to teleport to Ghost Land.",
    pickaxe: "A sturdy mining pickaxe. Keep it in your bag to break rocks in the mine.",
    grass_cutter: "A pair of garden shears. Keep them in your bag to cut grass tufts around the city for cash.",
    ore_gold: "A glittering chunk of gold ore. Sell it to the miner for $1000.",
    ore_silver: "A vein of silver ore. Sell it to the miner for $200.",
    ore_copper: "A ruddy lump of copper ore. Sell it to the miner for $60.",
    ore_iron: "Rough iron ore. Sell it to the miner for $50.",
    ore_stone: "A plain lump of stone. Sell it to the miner for $30.",
    sword: "A plain iron sword. Equip it to a hand, then press J to enter combat (outside the city).",
    round_shield: "A sturdy round shield (block 40). Equip it to your second hand; raise a 2s block to fully negate lighter blows — and stagger the attacker. Heavier hits pierce for the excess.",
    skinning_knife: "A keen skinning knife. Keep it in your bag to skin slain beasts for hides.",
    hog_hide: "A coarse hog hide. Leather Smiths want these — hand 20 to the skinner to learn the craft.",
    leather_low: "A sheet of low-grade leather, cured from raw hides. The Leather Smith's first product.",
    copper_bar: "A bar of smelted copper. A Black Smith's basic stock for forging.",
    iron_bar: "A bar of smelted iron, dense and true. Solid forging stock.",
    silver_bar: "A bar of gleaming smelted silver. Fine forging stock.",
    gold_bar: "A heavy bar of pure smelted gold. A master smith's prize.",
    leather_top_str: "A brown leather body suit, hand-cured by a Leather Smith. Equip it for protection.",
    leather_top_dex: "A brown leather body suit, hand-cured by a Leather Smith. Equip it for protection.",
    leather_top_agi: "A brown leather body suit, hand-cured by a Leather Smith. Equip it for protection.",
    leather_pants_str: "Sturdy brown leather pants, hand-cured by a Leather Smith. Equip them for protection.",
    leather_pants_dex: "Sturdy brown leather pants, hand-cured by a Leather Smith. Equip them for protection.",
    leather_pants_agi: "Sturdy brown leather pants, hand-cured by a Leather Smith. Equip them for protection.",
    armored_leather_top_str: "A reinforced leather body suit, studded for battle. Equip it for protection.",
    armored_leather_top_dex: "A reinforced leather body suit, studded for battle. Equip it for protection.",
    armored_leather_top_agi: "A reinforced leather body suit, studded for battle. Equip it for protection.",
    armored_leather_pants_str: "Reinforced studded leather pants. Equip them for protection.",
    armored_leather_pants_dex: "Reinforced studded leather pants. Equip them for protection.",
    armored_leather_pants_agi: "Reinforced studded leather pants. Equip them for protection.",
    blunt: "A fat hand-rolled blunt. Use it to kick back and smoke for 20s, recovering health as you chill.",
    fries: "A carton of salty fries, cooked from a potato. Eat them to restore 25 hunger.",
    water: "A bottle of cold water. Drink it to restore 50 thirst.",
    soda: "A fizzy soda. Drink it to restore 50 thirst.",
    hog_burger: "A hearty Watabaga hog burger, cooked from fresh hog meat. Eat it to restore 60 hunger.",
    potato: "A raw potato — the cooking pot turns one into a carton of fries.",
    hog_meat: "A slab of raw hog meat, cut while skinning. Good eating once cooked.",
    hog_charm: "A rare charm carved from a hog's bone. Prized by collectors.",
    hog_tusk: "A curved hog tusk. A common trophy from the wilds.",
    book_running: "Teach you how to run faster",
    goblin_ear: "A severed goblin ear. A trophy from the deeper wilds.",
    book_goblin_tech: "A grubby tome scrawled with crude schematics. The goblins are smarter than they look.",
    magic_dust: "A pinch of shimmering arcane dust, shaken loose from a slime. It tingles faintly.",
    fishing_rod: "A simple fishing rod.",
    worms: "Wriggling bait worms. Fish love 'em.",
    bmx: "A sturdy street BMX. Use it to drop it at your feet, then hop on with E.",
    balaclava: "A charcoal knit balaclava — only the eyes show. Equip to wear it (covers your hair).",
    fishing_hat: "A khaki canvas bucket hat.",
    mudfish: "A common, bottom-feeding mudfish. Sell it to a vendor for a few coins.",
    pondfish: "A small, everyday pondfish. Every angler's first catch.",
    moss_perch: "A moss-backed perch that hides among the weeds. A skilled angler's prize.",
    sun_carp: "A blazing golden carp said to swallow sunlight. Rare and prized.",
    bog_carp: "A wary bog carp that fought hard on the line. Worth a fair bit.",
    ghost_koi: "A pale, faintly glowing koi from the deep water. Vanishingly rare.",
    bed: "A cosy single bed. Use it inside a property you own to place it, then click it to move or pick it up.",
    safebox: "A heavy steel safebox — 20 slots, 200kg. Use it inside a property you own to place it; only the owner, staff, and key holders can open it.",
    furnace: "A brick smelting furnace. Place it inside your property — a Black Smith standing beside it can smelt ore into metal bars.",
    anvil: "A solid steel anvil, ready for a Black Smith's hammer. Place it beside your furnace — forging recipes will land on it.",
    weed_seed: "A pungent little seed.",
    watering_pot: "A tin watering pot holding 500ml.",
    weed_bud: "A sticky, aromatic weed bud, trimmed from a mature plant. Roll 3 with a Rolling Paper.",
    rolling_paper: "A thin king-size rolling paper.",
};
export const COSMETIC_SLOTS = ["cosHat", "cosTop", "cosBottom", "cosShoes"];
/** The cosmetic-slot field name for a gear EquipSlot (hat → cosHat, …). Mask/back have NO
 *  cosmetic counterpart (they're visual-only slots already) — callers gate on this. */
export function cosmeticSlotField(slot) {
    return slot === "hat" ? "cosHat" : slot === "top" ? "cosTop" : slot === "bottom" ? "cosBottom" : slot === "shoes" ? "cosShoes" : null;
}
/** The gear EquipSlot a cosmetic slot overrides (cosHat → hat, …). */
export function cosmeticSlotToEquip(slot) {
    return (slot === "cosHat" ? "hat" : slot === "cosTop" ? "top" : slot === "cosBottom" ? "bottom" : "shoes");
}
export const ITEM_EQUIP = {
    black_shirt: { slot: "top", styleId: 1, colorId: 1 },
    blue_jeans: { slot: "bottom", styleId: 1, colorId: 2 },
    black_cap: { slot: "hat", styleId: 1, colorId: 1 },
    // Colour caps — recolourable hat style 1, colourways matching the tees.
    white_cap: { slot: "hat", styleId: 1, colorId: 0 },
    blue_cap: { slot: "hat", styleId: 1, colorId: 2 },
    green_cap: { slot: "hat", styleId: 1, colorId: 5 },
    red_cap: { slot: "hat", styleId: 1, colorId: 6 },
    purple_cap: { slot: "hat", styleId: 1, colorId: 7 },
    black_shoes: { slot: "shoes", styleId: 1, colorId: 1 },
    // Pre-coloured garments: colorId 0 = "draw the sheet as rendered" (no recolour).
    ghost_tee: { slot: "top", styleId: 2, colorId: 0 },
    balaclava: { slot: "mask", styleId: 1, colorId: 0 }, // pre-coloured charcoal knit; hides hair (C3D_MASK)
    fishing_hat: { slot: "hat", styleId: 4, colorId: 0 }, // pre-coloured khaki bucket hat (C3D_HAT); boosts fishing
    work_pants: { slot: "bottom", styleId: 2, colorId: 0 },
    // Basic tees — the recolourable tee (top style 1) in each colourway (colorId → C3D_GARMENT_COLORS).
    white_tee: { slot: "top", styleId: 1, colorId: 0 },
    blue_tee: { slot: "top", styleId: 1, colorId: 2 },
    green_tee: { slot: "top", styleId: 1, colorId: 5 },
    red_tee: { slot: "top", styleId: 1, colorId: 6 },
    purple_tee: { slot: "top", styleId: 1, colorId: 7 },
    circle_shirt: { slot: "top", styleId: 3, colorId: 0 }, // recolourable circle-print tee (top style 3)
    pickaxe_tee: { slot: "top", styleId: 8, colorId: 0 }, // pre-coloured sheet (white tee + pickaxe print)
    bomber_jacket: { slot: "top", styleId: 9, colorId: 0 }, // pre-coloured sheet (black jacket + silver zip)
    // Kapon Sporty jumpsuit — the tech top (style 4) + tech pants (bottom style 3), blue & purple.
    kapon_sweatshirt: { slot: "top", styleId: 4, colorId: 2 },
    kapon_sweatpants: { slot: "bottom", styleId: 3, colorId: 2 },
    purple_sweatshirt: { slot: "top", styleId: 4, colorId: 7 },
    purple_sweatpants: { slot: "bottom", styleId: 3, colorId: 7 },
    // Crafted leather armor — the recolourable tech set (top 4 / pants 3) tinted brown. Each stat
    // variant uses a DISTINCT brown shade (3 Leather / 8 Dark Leather / 4 Tan) so the (slot,style,color)
    // triple stays unique for the unequip reverse-lookup (equipToItem).
    // Leather TOP uses the TEE (style 1) — it has full idle/walk/run + ATTACK + BLOCK sheets. (The tech
    // top, style 4, has no attack/block art, so its swing dropped the garment.) Bottom = techpants (3),
    // also fully animated. Three brown colourways for the 3 stat variants.
    leather_top_str: { slot: "top", styleId: 1, colorId: 3 },
    leather_top_dex: { slot: "top", styleId: 1, colorId: 8 },
    leather_top_agi: { slot: "top", styleId: 1, colorId: 4 },
    leather_pants_str: { slot: "bottom", styleId: 3, colorId: 3 },
    leather_pants_dex: { slot: "bottom", styleId: 3, colorId: 8 },
    leather_pants_agi: { slot: "bottom", styleId: 3, colorId: 4 },
    // Armored leather = the SAME styles as normal (tee + techpants) but DEEPER brown colourways (9/10/11),
    // so it looks like the normal piece, just darker. Distinct colorIds keep equipToItem unique. The base
    // stats match the normal piece; the 3 rolled bonus points ride along as per-instance `mods`.
    armored_leather_top_str: { slot: "top", styleId: 1, colorId: 9 },
    armored_leather_top_dex: { slot: "top", styleId: 1, colorId: 10 },
    armored_leather_top_agi: { slot: "top", styleId: 1, colorId: 11 },
    armored_leather_pants_str: { slot: "bottom", styleId: 3, colorId: 9 },
    armored_leather_pants_dex: { slot: "bottom", styleId: 3, colorId: 10 },
    armored_leather_pants_agi: { slot: "bottom", styleId: 3, colorId: 11 },
};
/** The THREADS clothing store stock. Basic tees are cheap; the Kapon Sporty jumpsuit is luxury.
 *  The Ghost Tee is displayed but NOT for sale (a relic, not merchandise). */
export const THREADS_SHOP = [
    { itemId: "white_tee", price: 500, forSale: true },
    { itemId: "black_shirt", price: 500, forSale: true },
    { itemId: "red_tee", price: 500, forSale: true },
    { itemId: "green_tee", price: 500, forSale: true },
    { itemId: "blue_tee", price: 500, forSale: true },
    { itemId: "purple_tee", price: 500, forSale: true },
    { itemId: "circle_shirt", price: 500, forSale: true },
    { itemId: "blue_jeans", price: 500, forSale: true },
    { itemId: "work_pants", price: 500, forSale: true },
    { itemId: "black_cap", price: 400, forSale: true },
    { itemId: "white_cap", price: 400, forSale: true },
    { itemId: "red_cap", price: 400, forSale: true },
    { itemId: "green_cap", price: 400, forSale: true },
    { itemId: "blue_cap", price: 400, forSale: true },
    { itemId: "purple_cap", price: 400, forSale: true },
    { itemId: "bomber_jacket", price: 5000, forSale: true },
    { itemId: "black_shoes", price: 1000, forSale: true },
    { itemId: "kapon_sweatshirt", price: 100000, forSale: true },
    { itemId: "kapon_sweatpants", price: 100000, forSale: true },
    { itemId: "purple_sweatshirt", price: 100000, forSale: true },
    { itemId: "purple_sweatpants", price: 100000, forSale: true },
    { itemId: "ghost_tee", price: 0, forSale: false },
];
/** The BLACK MARKET stock — a hooded trader dealing greener goods for cold cash. (Weapons moved
 *  to the legitimate BLACKSMITH shop on the residence row.) */
export const BLACKMARKET_SHOP = [
    // Blunts aren't sold ready-made — roll your own: seed → grow → harvest → paper.
    { itemId: "weed_seed", price: 600, forSale: true },
    { itemId: "watering_pot", price: 1000, forSale: true },
    { itemId: "rolling_paper", price: 250, forSale: true, qty: 10 }, // a $250 pack = 10 papers
];
/** The BLACKSMITH shop (residence row, next to the warehouse; owned by Ghost like every city
 *  building): honest steel over back-alley deals — plus Ember teaches the Black Smith profession
 *  for 20 copper ore. Sales land in the shop's register. */
export const SMITH_BUILDING_ID = "smith";
export const SMITH_SHOP = [
    { itemId: "sword", price: 7000, forSale: true },
    { itemId: "round_shield", price: 5000, forSale: true },
];
/** GHOST LAB retail — Ghost's own tech: the rideable BMX. */
export const GHOSTLAB_SHOP = [
    { itemId: "bmx", price: 7500, forSale: true },
    { itemId: "balaclava", price: 2500, forSale: true },
    { itemId: "fishing_hat", price: 4000, forSale: true },
];
/** The SKINNER's wares (Winston's-island NPC): the skinning knife that enables the skinning skill. */
export const SKINNER_SHOP = [
    { itemId: "skinning_knife", price: 5000, forSale: true },
];
/** OLD TIMER'S BAITS (the tackle shed behind Okka Furniture): the fishing rod finally has an
 *  in-game source, plus bait worms. */
export const TACKLE_SHOP = [
    { itemId: "fishing_rod", price: 3000, forSale: true },
    { itemId: "worms", price: 25, forSale: true, qty: 25 }, // a $25 tin = 25 baits
];
/** The FOOD STORE catalogue: every item Watabaga can resell + its DEFAULT price (~2× cost, so a fresh
 *  store draws customers at the fast cadence). Prices are per-building adjustable (≥ foodCost); the owner
 *  picks which items the NPC offers (Building.menu). */
export const FOOD_SHOP = [
    { itemId: "fries", price: 30, forSale: true },
    { itemId: "water", price: 20, forSale: true },
    { itemId: "soda", price: 20, forSale: true },
    { itemId: "hog_burger", price: 60, forSale: true },
];
/** Hunger/thirst restored when a food item is consumed (via UseItem). */
export const FOOD_RESTORE = {
    fries: { hunger: 25 },
    water: { thirst: 50 },
    soda: { thirst: 50 },
    hog_burger: { hunger: 60 },
};
/** Raw kitchen supplies the Watabaga vendor sells (so staff can cook). Bought with cash via handleBuy.
 *  Kept cheap so cooking + stocking fries is net-positive for the worker (potato $5 → stock fries +$15). */
export const WATABAGA_SUPPLIES = [
    { itemId: "potato", price: 5, forSale: true },
];
export function isFood(itemId) {
    return Object.prototype.hasOwnProperty.call(FOOD_RESTORE, itemId);
}
// ── Furniture (Okka Furniture) ───────────────────────────────────────────────
/** The furniture store building id (SE corner of the city, opposite the apartment tenement). */
export const FURNI_BUILDING_ID = "furni";
/** The OKKA FURNITURE catalogue — the clerk sells these; every sale credits the store register. */
export const FURNITURE_SHOP = [
    { itemId: "bed", price: 5000, forSale: true },
    { itemId: "safebox", price: 10000, forSale: true },
    // The Black Smith's home workshop — the furnace smelts (see STATION_FURNITURE); the anvil
    // is the forging surface (recipes to come).
    { itemId: "furnace", price: 10000, forSale: true },
    { itemId: "anvil", price: 10000, forSale: true },
];
/** Placeable furniture item ids. */
export const FURNITURE_IDS = ["bed", "safebox", "furnace", "anvil"];
export function isFurnitureItem(id) { return FURNITURE_IDS.includes(id); }
export const SAFEBOX_ID = "safebox";
/** Most furniture pieces one building (apartment or property) can hold. */
export const MAX_FURNITURE_PER_BUILDING = 20;
export const FURNITURE_FUNCTIONS = {
    bed: { action: "sleep", label: "Sleep" },
    safebox: { action: "storage", label: "Open" },
    furnace: { action: "smelt", label: "Smelt" },
};
/** How close (server px) a player must stand to use a furniture function (sleep in a bed…). */
export const FURNITURE_USE_RANGE = 96;
/** Placed-furniture footprint radii (world units). Two pieces must keep r1+r2 apart in 2D — a
 *  circle bound (rotation-proof), checked at ANY height, so pieces can't overlap OR stack. */
export const FURNITURE_RADIUS = {
    bed: 0.8,
    safebox: 0.45,
    furnace: 0.55,
    anvil: 0.4,
};
export function furnitureRadius(id) { return FURNITURE_RADIUS[id] ?? 0.5; }
/** Resting in a bed heals this fraction of max health every BED_HEAL_INTERVAL_MS while resting. */
export const BED_HEAL_FRACTION = 0.1;
export const BED_HEAL_INTERVAL_MS = 3000;
/** Safebox storage: 20 slots, 200kg (every item = 1kg — see ITEM_WEIGHT_KG). */
export const SAFEBOX_SLOTS = 20;
export const SAFEBOX_CAPACITY_KG = 200;
/** How high (world units above the room floor) furniture may be raised while placing. */
export const FURNITURE_MAX_Z = 2;
/** COARSE per-building rects (server px) that placed furniture must stay inside — a server-side sanity
 *  bound; the client's placement mode clamps precisely to the zone's walkable bounds. Interiors mirror
 *  streetScene's far-away room centres ± half size; each tenement's homes share their building's
 *  footprint (villageScene: apartment blocks at tiles 54,49 and 70,49, 14×10 each). Placed furniture
 *  is persisted RELATIVE to each rect's centre (see furnishCenter), so moving a building here + in
 *  villageScene together carries its saved furniture along. */
const FURNISH_RECTS = {
    tavern: { cx: 10, cy: 30, hx: 7, hy: 6 },
    citytavern: { cx: 230, cy: 70, hx: 7, hy: 6 },
    threads: { cx: 110, cy: 70, hx: 7, hy: 5.5 },
    casino: { cx: 150, cy: 70, hx: 13, hy: 13 },
    bank: { cx: 190, cy: 70, hx: 7, hy: 6 },
    food: { cx: 270, cy: 70, hx: 6.5, hy: 5 },
    ghosts: { cx: 310, cy: 70, hx: 6.5, hy: 5 },
    furni: { cx: 470, cy: 70, hx: 8, hy: 6.5 },
    tackle: { cx: 510, cy: 70, hx: 4, hy: 3.25 },
    smith: { cx: 670, cy: 70, hx: 5, hy: 4 },
    // The tenement footprints (NW residential corner) — MUST mirror buildVillage's apartment()
    // placements (first block centre 54,49; the ANNEX centre 70,49 · half 7,5 each). Saved furniture
    // is stored relative to these centres, so a move here + in villageScene carries it along.
    apt2a: { cx: 54, cy: 49, hx: 7, hy: 5 },
    apt2b: { cx: 54, cy: 49, hx: 7, hy: 5 },
    apt3a: { cx: 54, cy: 49, hx: 7, hy: 5 },
    apt3b: { cx: 54, cy: 49, hx: 7, hy: 5 },
    anx2a: { cx: 70, cy: 49, hx: 7, hy: 5 },
    anx2b: { cx: 70, cy: 49, hx: 7, hy: 5 },
    anx3a: { cx: 70, cy: 49, hx: 7, hy: 5 },
    anx3b: { cx: 70, cy: 49, hx: 7, hy: 5 },
};
/** The furnishable area of a building (server px), or null if it can't hold furniture. */
export function furnishBounds(bid) {
    const r = FURNISH_RECTS[bid];
    if (!r)
        return null;
    return { x0: (r.cx - r.hx) * 32, y0: (r.cy - r.hy) * 32, x1: (r.cx + r.hx) * 32, y1: (r.cy + r.hy) * 32 };
}
/** The centre (server px) of a building's furnishable area — the origin placed furniture is persisted
 *  RELATIVE to, so relocating a building keeps its furniture in the same spot inside it. Buildings
 *  without a rect fall back to the world origin (their entries persist as absolute coords). */
export function furnishCenter(bid) {
    const r = FURNISH_RECTS[bid];
    return r ? { x: r.cx * 32, y: r.cy * 32 } : { x: 0, y: 0 };
}
// ── Business buildings: store chest + NPC resale + staff + register + walk-in customers ──────────
/** The sellable clothing catalogue — the goods Threads can stock + resell (drops the not-for-sale
 *  ghost_tee relic). The clothing equivalent of FOOD_SHOP. */
export const THREADS_CATALOGUE = THREADS_SHOP.filter((e) => e.forSale);
/** The resell catalogue for a business building: food for restaurants, clothing for Threads. */
export function businessCatalogue(bid) {
    return bid === "threads" ? THREADS_CATALOGUE : FOOD_SHOP;
}
/** The default NPC menu (catalogue item ids) for a business building. */
export function defaultMenu(bid) {
    return businessCatalogue(bid).map((e) => e.itemId);
}
/** Is this item in the building's resell catalogue (i.e. the NPC can sell it)? Stocking one into the
 *  store chest pays the worker. */
export function isMenuItem(bid, itemId) {
    return businessCatalogue(bid).some((e) => e.itemId === itemId);
}
/** Default NPC sale price for an item at a building ($0 if not in its catalogue). */
export function menuPrice(bid, itemId) {
    return businessCatalogue(bid).find((e) => e.itemId === itemId)?.price ?? 0;
}
/** Default per-task WAGE for stocking an item (paid from the register + the floor for its price).
 *  Restaurants use FOOD_COST; clothing defaults to a fraction of the catalogue price, so stocking
 *  stays net-positive and a fresh register can cover it. The owner overrides this per building. */
export function menuCost(bid, itemId) {
    if (bid === "threads")
        return Math.round(menuPrice(bid, itemId) * 0.15);
    return FOOD_COST[itemId] ?? 0;
}
/** A city's benchmark price for an item at a building — what walk-in customers judge prices against
 *  (≤ it ⇒ customers flock; ≥ 2× ⇒ they stay away). Food uses CITY_AVG_PRICE; clothing uses its
 *  catalogue price (so a default-priced store draws customers at the fast cadence). */
export function cityAvgPrice(bid, itemId) {
    if (bid === "threads")
        return menuPrice(bid, itemId);
    return CITY_AVG_PRICE[itemId] ?? 0;
}
/** Look up a buyable item across ALL shops (used by the server to validate a purchase). */
export function shopEntry(itemId) {
    return THREADS_SHOP.find((e) => e.itemId === itemId)
        ?? BLACKMARKET_SHOP.find((e) => e.itemId === itemId)
        ?? SMITH_SHOP.find((e) => e.itemId === itemId)
        ?? GHOSTLAB_SHOP.find((e) => e.itemId === itemId)
        ?? SKINNER_SHOP.find((e) => e.itemId === itemId)
        ?? TACKLE_SHOP.find((e) => e.itemId === itemId)
        ?? FOOD_SHOP.find((e) => e.itemId === itemId)
        ?? WATABAGA_SUPPLIES.find((e) => e.itemId === itemId)
        ?? FURNITURE_SHOP.find((e) => e.itemId === itemId);
}
export function itemEquip(id) {
    return ITEM_EQUIP[id];
}
/** Reverse lookup: which item id corresponds to an equipped (slot, style, color)? */
export function equipToItem(slot, styleId, colorId) {
    if (!styleId)
        return null;
    for (const id of ITEM_IDS) {
        const e = ITEM_EQUIP[id];
        if (e && e.slot === slot && e.styleId === styleId && e.colorId === colorId)
            return id;
    }
    return null;
}
/** Inventory geometry and stacking rules. */
export const INVENTORY_COLS = 5;
export const INVENTORY_ROWS = 4;
export const INVENTORY_CAPACITY = INVENTORY_COLS * INVENTORY_ROWS; // 20 slots
export const MAX_STACK = 99;
/** The 6 armored-leather ids (top/pants × str/dex/agi) — unique per-instance (carry rolled `mods`). */
export const ARMORED_LEATHER_IDS = [
    "armored_leather_top_str", "armored_leather_top_dex", "armored_leather_top_agi",
    "armored_leather_pants_str", "armored_leather_pants_dex", "armored_leather_pants_agi",
];
/** Items that are UNIQUE per-instance (carry their own data — pet level or rolled mods) and never stack.
 *  Furniture is bulky (one per slot) so it never stacks either. */
export const NON_STACKABLE_ITEMS = new Set([
    HOG_CHARM_ID, ...ARMORED_LEATHER_IDS, "bed", "safebox",
    // Caught fish are unique per-instance: each carries its own rolled `size`, so they never stack.
    "mudfish", "pondfish", "bog_carp", "moss_perch", "ghost_koi", "sun_carp",
    // The fishing rod is unique per-instance too: it carries its own loaded `bait` count.
    "fishing_rod",
    // The watering pot is unique per-instance: it carries its own `water` (ml) tank.
    "watering_pot",
]);
/** Max stack size for an item — 1 for unique per-instance items, else MAX_STACK. */
export function maxStackFor(itemId) {
    return NON_STACKABLE_ITEMS.has(itemId) ? 1 : MAX_STACK;
}
/** Whether an item auto-rolls PET stats when added (the Hog Charm) — distinct from armored, which
 *  carries explicit `mods` set at craft time. */
export function isPetItem(itemId) { return itemId === HOG_CHARM_ID; }
/** Whether an item carries per-instance armored `mods` (rolled bonus points). */
export function isModdedGear(itemId) { return ARMORED_LEATHER_IDS.includes(itemId); }
/** The armored id for a normal leather variant (e.g. leather_top_str → armored_leather_top_str), or
 *  null if it isn't a normal leather piece. */
export function armoredVariantOf(itemId) {
    const a = `armored_${itemId}`;
    return ARMORED_LEATHER_IDS.includes(a) ? a : null;
}
/** Normal leather variants of a family, for the armored upgrade recipe's "any leather top/pants" input. */
export const LEATHER_FAMILIES = {
    leather_top: ["leather_top_str", "leather_top_dex", "leather_top_agi"],
    leather_pants: ["leather_pants_str", "leather_pants_dex", "leather_pants_agi"],
};
/** Bank vault item STORAGE — a personal, upgradable second inventory kept at the bank (money is
 *  already unlimited via the bank balance). Capacity grows one row per purchased tier. */
export const STORAGE_COLS = 5;
export const STORAGE_BASE_ROWS = 2; // tier 0 = 10 slots
export const STORAGE_MAX_TIER = 4; // up to 6 rows = 30 slots
/** Slots available at a given upgrade tier. */
export function storageCapacity(tier) {
    const t = Math.max(0, Math.min(STORAGE_MAX_TIER, Math.floor(tier)));
    return STORAGE_COLS * (STORAGE_BASE_ROWS + t);
}
/** Cost (bank money) to buy the NEXT tier from `tier`, or null when already maxed. */
export function storageUpgradeCost(tier) {
    if (tier >= STORAGE_MAX_TIER)
        return null;
    return 5000 * Math.pow(2, tier); // 5k → 10k → 20k → 40k
}
/** Pickup detection radius in pixels (~1.5 tiles). */
export const PICKUP_RANGE = 24;
/** A DROPPED ground item (player drop / death drop) despawns this long after it lands if no one
 *  picks it up. Seeded world items are exempt (they persist with the room). */
export const GROUND_ITEM_TTL_MS = 300000; // 5 minutes
/**
 * Currency. `balance` lives on the character (persisted) and is the stake for the
 * blackjack table. New characters start with STARTING_BALANCE; coins picked up in
 * the world credit it. Coins are ground items with a reserved id — deliberately NOT
 * in `ITEM_IDS`, so they can never land in the inventory grid; pickup credits the
 * balance instead.
 */
export const STARTING_BALANCE = 200; // starting CASH (carried)
export const COIN_ID = "coin";
export const COIN_VALUE = 25;
/** Day/night cycle: real milliseconds for one full 24h in-game day. The time-of-day is a pure function
 *  of the wall clock (below), so every client + the server agree — and a relogin lands on the right
 *  time, not a reset. ~16 real minutes per day. */
export const DAY_LENGTH_MS = 16 * 60 * 1000;
/** Time of day (0..24h) at a given epoch (ms). Shared by server + client so the cycle stays in sync. */
export function timeOfDayAt(epochMs) {
    return ((epochMs % DAY_LENGTH_MS) / DAY_LENGTH_MS) * 24;
}
/** Ground-item id for a dropped pile of cash (carries its value in `GroundItem.amount`). On death a
 *  player drops ALL their carried cash as one of these; picking it up credits the wallet by `amount`.
 *  Not a real inventory item (never in ITEM_IDS) — it goes straight to the wallet, like a coin. */
export const CASH_DROP_ID = "cash";
/** Bank money starts empty (it's the unlimited stored balance; cash is carried). */
export const STARTING_BANK = 0;
/** Carry weight: every inventory item weighs 1kg and the player can carry up to 100kg. */
export const ITEM_WEIGHT_KG = 1;
export const MAX_CARRY_KG = 100;
/**
 * Mining. Rock NODES spawn over time (server-authoritative, shared). A node's `kind` is its TIER
 * (stone→copper→iron→silver→gold); each hit yields one ore of that kind. Mining mirrors the
 * skinning ladder (see docs/design/skills-and-professions.md): tier level-bands, the same per-5-level
 * channel-time reward, XP only on your current tier, and you can't mine a tier above your level.
 * The Ghost-Land CITY quarry spawns STONE ONLY; the higher tiers spawn in the Kapon wilds.
 */
export const MINE_KINDS = ["stone", "copper", "iron", "silver", "gold"];
/** The ore item a node of each kind yields. */
export const MINE_KIND_ORE = {
    stone: "ore_stone", copper: "ore_copper", iron: "ore_iron", silver: "ore_silver", gold: "ore_gold",
};
/** Sell value (cash) per ore. */
export const ORE_VALUE = { ore_gold: 1000, ore_silver: 200, ore_iron: 50, ore_copper: 60, ore_stone: 30 };
// --- vendor NPC (sell-to-NPC for cash) --------------------------------------
/** REFERENCE sheet of item worth. The vendor actually pays NPC_SELL_RATE of this (see npcSellValue).
 *  Items absent here are NOT sellable to the vendor (e.g. tools, the teleport scroll). The player
 *  marketplace is where you get real value; the vendor is a junk buyer who lowballs. */
export const NPC_SELL_VALUE = {
    // ores — match the miner's buy prices
    ore_gold: 1000, ore_silver: 200, ore_iron: 50, ore_copper: 60, ore_stone: 30,
    // beast drops + materials
    hog_hide: 15, hog_meat: 8, hog_tusk: 25, hog_charm: 200,
    goblin_ear: 30, magic_dust: 50, book_goblin_tech: 150,
    // fish (vendor lowballs; the real value is selling/showing them off)
    mudfish: 40, pondfish: 30, bog_carp: 140, moss_perch: 240, sun_carp: 600, ghost_koi: 1600,
    // smelted bars — worth 2× their ore (the smelting risk pays; copper set the precedent)
    leather_low: 20, copper_bar: 120, iron_bar: 100, silver_bar: 400, gold_bar: 2000,
    // crafted leather armor (all stat variants)
    leather_top_str: 100, leather_top_dex: 100, leather_top_agi: 100,
    leather_pants_str: 100, leather_pants_dex: 100, leather_pants_agi: 100,
    armored_leather_top_str: 250, armored_leather_top_dex: 250, armored_leather_top_agi: 250,
    armored_leather_pants_str: 250, armored_leather_pants_dex: 250, armored_leather_pants_agi: 250,
    // clothing
    black_shirt: 30, blue_jeans: 30, black_cap: 30, black_shoes: 30, work_pants: 20,
    white_cap: 30, red_cap: 30, green_cap: 30, blue_cap: 30, purple_cap: 30, circle_shirt: 50,
    white_tee: 50, red_tee: 50, green_tee: 50, blue_tee: 50, purple_tee: 50,
    pickaxe_tee: 50, bomber_jacket: 500,
    kapon_sweatshirt: 5000, kapon_sweatpants: 5000, purple_sweatshirt: 5000, purple_sweatpants: 5000,
    // weapons / consumables / misc
    sword: 1000, round_shield: 800, blunt: 100, book_running: 50,
    fries: 5, water: 10, soda: 10, hog_burger: 8, potato: 3,
};
/** Fraction of the reference sheet price the vendor actually pays (he lowballs at 30%). */
export const NPC_SELL_RATE = 0.30;
/** Cash the vendor pays per unit of `itemId` — 30% of the sheet price, min $1 for listed items
 *  (0 = not sellable). */
/** Fish sell at a better rate — Marla the fish buyer pays half their reference worth. */
export const FISH_SELL_RATE = 0.50;
export function npcSellValue(itemId) {
    const base = NPC_SELL_VALUE[itemId] ?? 0;
    const rate = isFishItem(itemId) ? FISH_SELL_RATE : NPC_SELL_RATE;
    return base > 0 ? Math.max(1, Math.round(base * rate)) : 0;
}
// --- player marketplace -----------------------------------------------------
/** Most active listings one character may have at once. */
export const MARKET_MAX_LISTINGS = 10;
/** Upfront listing fee, as a fraction of the asking price (charged from the seller's BANK on list). */
export const MARKET_FEE_RATE = 0.02;
/** The fee (bank money) to list at `price` — 2% of the asking price, min $1. */
export function marketListingFee(price) {
    return Math.max(1, Math.ceil(price * MARKET_FEE_RATE));
}
/** Sanity bounds for an asking price + how many listings the browse snapshot returns. */
export const MARKET_MIN_PRICE = 1;
export const MARKET_MAX_PRICE = 1_000_000_000;
export const MARKET_SNAPSHOT_MAX = 300;
export const MINE_NODE_HITS = 5; // mines per node before it's depleted
export const MINE_MAX_NODES = 6; // most CITY (stone) nodes alive at once
export const MINE_SPAWN_INTERVAL_MS = 60000; // a new city node every minute (up to the cap)
export const MINE_RANGE = 40; // server px: how close to a node to mine it
/** City quarry (Ghost Land "village" zone) in server px — STONE ONLY. Lives in the city's NE
 *  NATURE quarter, east of the pond (mirrors buildVillage's minePatch at 93,49). */
export const MINE_AREA = { x0: 89 * 32, y0: 44 * 32, x1: 97 * 32, y1: 54 * 32 };
/** Kapon wilds ore field — the higher tiers (copper/iron/silver/gold) spawn here. */
export const MINE_AREA_KAPON = { x0: 54 * 32, y0: -104 * 32, x1: 86 * 32, y1: -76 * 32 }; // spread across the wider clearing
export const MINE_MAX_NODES_KAPON = 5; // few ore nodes — capped low so copper stays scarce
export const MINE_SPAWN_INTERVAL_KAPON_MS = 45000;
/** Kapon ore spawn weights (sums to 1) — the Kapon field is COPPER ONLY by design; the higher
 *  tiers (iron/silver/gold) will spawn in future, deeper regions. Weighted-roll kept so adding
 *  a tier later is a number change here, not code. */
export const MINE_KAPON_WEIGHTS = { copper: 1, iron: 0, silver: 0, gold: 0 };
/** Shared leveling curve — XP to go from `level`→`level+1` grows LINEARLY (gentle, WoW-style: the
 *  per-level cost rises by a flat slope each level, so TOTAL xp is ~quadratic, not the old ×2
 *  exponential blow-up). Used by every skill, base stat, AND profession track so they all level the
 *  same way. e.g. L1→2 = 60, L9→10 = 540; cumulative to L10 = 2,700, to L50 = 73,500. */
export const XP_PER_LEVEL_SLOPE = 60;
export function xpToNextLevel(level) {
    return XP_PER_LEVEL_SLOPE * Math.max(1, Math.floor(level));
}
/** XP to advance mining from `level`→`level+1` (60×L — see xpToNextLevel). */
export const MINING_LEVEL_CAP = 100;
export function pickaxeXpForLevel(level) { return xpToNextLevel(level); }
export const MINE_TIERS = [
    { kind: "stone", bandStart: 1, bandEnd: 10, unlock: 1, xp: 3 },
    { kind: "copper", bandStart: 11, bandEnd: 20, unlock: 10, xp: 6 },
    { kind: "iron", bandStart: 21, bandEnd: 30, unlock: 20, xp: 9 },
    { kind: "silver", bandStart: 31, bandEnd: 40, unlock: 30, xp: 12 },
    { kind: "gold", bandStart: 41, bandEnd: 50, unlock: 40, xp: 15 },
];
export function mineTierInfo(kind) { return MINE_TIERS.find((t) => t.kind === kind) ?? MINE_TIERS[0]; }
/** The tier whose XP a player at `level` currently earns — the highest tier they've unlocked. */
export function currentMineTier(level) {
    let cur = "stone";
    for (const t of MINE_TIERS)
        if (level >= t.unlock)
            cur = t.kind;
    return cur;
}
/** Can the player mine a node of this tier? (You can't mine tiers above your level.) */
export function canMineTier(kind, level) { return level >= mineTierInfo(kind).unlock; }
/** XP is earned only from the player's CURRENT tier; lower tiers still give ore but 0 XP. */
export function mineGainsXp(kind, level) { return kind === currentMineTier(level); }
export function mineXpFor(kind) { return mineTierInfo(kind).xp; }
export const MINE_BASE_SECONDS = 15;
/** Mining channel ms for `kind` at `level`: 15s base, −3s at the 5th & 10th level of the tier's
 *  band, floored at 9s (mirrors skinning's per-5-level reward). */
export function mineDurationMs(kind, level) {
    const info = mineTierInfo(kind);
    let secs = MINE_BASE_SECONDS;
    if (level >= info.bandStart + 4)
        secs -= 3;
    if (level >= info.bandStart + 9)
        secs -= 3;
    return Math.max(MINE_BASE_SECONDS - 6, secs) * 1000;
}
export const DEFAULT_MINING = { level: 1, xp: 0 };
export const DEFAULT_FISHING = { level: 1, xp: 0 };
export const FISHING_LEVEL_CAP = 100;
/** XP to advance fishing from `level`→`level+1` (60×L — same curve as mining/skinning). */
export function fishingXpForLevel(level) { return xpToNextLevel(level); }
/** The catchable fish (also their item ids), rarity order white → yellow. */
export const FISH_KINDS = ["mudfish", "pondfish", "bog_carp", "moss_perch", "sun_carp", "ghost_koi"];
/** Fish RARITY tiers, lowest → highest. Higher rarity = better stats (XP, size, sell value) and a
 *  harder catch (later unlock, lower bite weight, faster/wilder in the radar minigame). */
export const FISH_RARITIES = ["white", "green", "blue", "purple", "yellow"];
export const FISH_RARITY = {
    mudfish: "white", pondfish: "white",
    bog_carp: "green",
    moss_perch: "blue",
    sun_carp: "purple",
    ghost_koi: "yellow",
};
/** Display color per rarity (item names, debug panel). White is the plain base tier. */
export const FISH_RARITY_COLORS = {
    white: "#f4f4f4", green: "#7ce0a0", blue: "#6db3f2", purple: "#b48ce0", yellow: "#ffd23f",
};
/** Fish tiers — rarer fish unlock later, give more XP, bite less often, and fight harder. */
export const FISH_TIERS = [
    { kind: "mudfish", unlock: 1, xp: 4, weight: 1.0, drain: 16, pull: 40 },
    { kind: "pondfish", unlock: 1, xp: 3, weight: 1.1, drain: 14, pull: 42 },
    { kind: "bog_carp", unlock: 15, xp: 11, weight: 0.5, drain: 26, pull: 36 },
    { kind: "moss_perch", unlock: 20, xp: 15, weight: 0.30, drain: 30, pull: 35 },
    { kind: "sun_carp", unlock: 30, xp: 24, weight: 0.22, drain: 38, pull: 32 },
    { kind: "ghost_koi", unlock: 45, xp: 40, weight: 0.07, drain: 44, pull: 30 },
];
export function fishTierInfo(kind) { return FISH_TIERS.find((t) => t.kind === kind) ?? FISH_TIERS[0]; }
/** Can the player land a fish of this tier at `level`? (Rolls above your level never bite.) */
export function canFishTier(kind, level) { return level >= fishTierInfo(kind).unlock; }
/** Fishing XP for landing `kind` at `level`. Low-rarity fish stop teaching much once you outgrow
 *  them: white XP HALVES above level 15, green above 30, blue above 45 (purple/yellow never do). */
export function fishXpFor(kind, level = 1) {
    const base = fishTierInfo(kind).xp;
    const falloff = { white: 15, green: 30, blue: 45 };
    const cut = falloff[FISH_RARITY[kind]];
    return cut !== undefined && level > cut ? Math.max(1, Math.round(base / 2)) : base;
}
/** Which fish live in each water body — EVERY kind, everywhere: fishing level never locks a fish,
 *  it only shifts the rarity odds (see FISH_RARITY_ODDS). Kept per-water for future tuning. */
export const WATER_FISH = {
    pond: ["mudfish", "pondfish"], // the CITY pond holds only the two basic WHITE fish
    lake: FISH_KINDS, // the Kapon lake holds every kind (rarity scales with level)
};
/** Rarity odds (percent, sum 100) by fishing-level bracket — the bracket whose `minLevel` you've
 *  reached applies. Rare tiers grow with level; whites never vanish; a level-1 angler still has a
 *  1% shot at a yellow. THIS is the only gate on rare fish — there are no hard unlocks. */
export const FISH_RARITY_ODDS = [
    { minLevel: 1, odds: { white: 82, green: 10, blue: 5, purple: 2, yellow: 1 } },
    { minLevel: 6, odds: { white: 72, green: 15, blue: 8, purple: 3.5, yellow: 1.5 } },
    { minLevel: 16, odds: { white: 61, green: 20, blue: 12, purple: 5, yellow: 2 } },
    { minLevel: 26, odds: { white: 48, green: 25, blue: 16, purple: 8, yellow: 3 } },
    { minLevel: 41, odds: { white: 35, green: 28, blue: 20, purple: 12, yellow: 5 } }, // final tier — applies from 41 up
];
export function fishRarityOdds(level) {
    let cur = FISH_RARITY_ODDS[0].odds;
    for (const b of FISH_RARITY_ODDS)
        if (level >= b.minLevel)
            cur = b.odds;
    return cur;
}
/** Roll which fish bites at `level`: first the RARITY tier (level-scaled odds), then which fish
 *  of that tier (by bite weight). If the water holds no fish of the rolled tier, step down tiers
 *  until one lives here. `rnd`/`rnd2` ∈ [0,1). */
export function rollFish(level, rnd, kinds = FISH_KINDS, rnd2 = Math.random()) {
    const odds = fishRarityOdds(Math.max(1, Math.floor(level)));
    let rolled = "white";
    let r = rnd * 100;
    for (const rar of FISH_RARITIES) {
        if ((r -= odds[rar]) < 0) {
            rolled = rar;
            break;
        }
    }
    // Nearest tier that actually lives in this water: step DOWN from the rolled rarity first,
    // then UP (a green-only water still yields its green on a white roll).
    const start = FISH_RARITIES.indexOf(rolled);
    const order = [
        ...FISH_RARITIES.slice(0, start + 1).reverse(),
        ...FISH_RARITIES.slice(start + 1),
    ];
    for (const rarity of order) {
        const pool = FISH_TIERS.filter((t) => kinds.includes(t.kind) && FISH_RARITY[t.kind] === rarity);
        if (!pool.length)
            continue;
        const total = pool.reduce((sum, t) => sum + t.weight, 0);
        let q = rnd2 * total;
        for (const t of pool)
            if ((q -= t.weight) < 0)
                return t.kind;
        return pool[pool.length - 1].kind;
    }
    return kinds[0] ?? "mudfish";
}
/** How close to water's edge (server px) the player must stand to cast. */
export const FISH_RANGE = 80;
/** The city POND (village NE nature quarter), in world tiles — a second fishable water body.
 *  MUST mirror villageScene's pond placement (same shape as KAPON_TERRAIN.lake: {x, z, r}). */
export const CITY_POND = { x: 82, z: 43, r: 5 };
/** The bite arrives after a random wait in this window (biased short; never more than 90s). */
export const FISH_BITE_MIN_MS = 5000;
export const FISH_BITE_MAX_MS = 90000;
/** The success bar runs 0..100; all the way UP lands the fish, all the way DOWN it's a miss. */
export const FISH_CATCH_FULL = 100;
/** Where the success bar starts when the fish first bites (the middle). */
export const FISH_CATCH_START = 50;
/** Per-fish RADAR minigame stats: `speed` = how fast the fish icon slides along the track,
 *  `energy` = how far each random dart can jump. Both 0..1 — harder fish are faster + wilder. */
export const FISH_GAME = {
    mudfish: { speed: 0.18, energy: 0.16 },
    pondfish: { speed: 0.13, energy: 0.12 },
    bog_carp: { speed: 0.35, energy: 0.32 },
    moss_perch: { speed: 0.48, energy: 0.45 },
    sun_carp: { speed: 0.65, energy: 0.60 },
    ghost_koi: { speed: 0.80, energy: 0.75 },
};
/** Per-fish SIZE range (inches). Every caught fish is a unique item instance carrying a rolled
 *  `size` within its kind's range — persisted with the item through storage, drops and trades. */
export const FISH_SIZES = {
    mudfish: { min: 3, max: 8 },
    pondfish: { min: 2, max: 6 },
    bog_carp: { min: 8, max: 18 },
    moss_perch: { min: 6, max: 14 },
    ghost_koi: { min: 14, max: 34 },
    sun_carp: { min: 10, max: 26 },
};
/** Whether an item is a caught fish (unique per-instance, carries a rolled `size`). */
export function isFishItem(itemId) {
    return FISH_KINDS.includes(itemId);
}
/**
 * Roll a fish's size: a BELL-CURVED draw over the kind's [min, max] range — the mean of three
 * uniform rolls (Bates n=3), so mid-range sizes are common and sizes near either end are rare
 * (a 3–6" fish almost never rolls 3.0 or 6.0). Rounded to 2 decimals, e.g. 3.54.
 */
export function rollFishSize(kind, rng = Math.random) {
    const { min, max } = FISH_SIZES[kind];
    const t = (rng() + rng() + rng()) / 3;
    return Math.round((min + t * (max - min)) * 100) / 100;
}
/** Validate/coerce a persisted fish `size` for an item: clamped to the kind's range, 2 decimals.
 *  Returns undefined for non-fish items or garbage input (callers then re-roll or omit). */
export function sanitizeFishSize(itemId, raw) {
    if (!isFishItem(itemId))
        return undefined;
    const n = Number(raw);
    if (!Number.isFinite(n))
        return undefined;
    const { min, max } = FISH_SIZES[itemId];
    return Math.round(Math.min(max, Math.max(min, n)) * 100) / 100;
}
/** The radar's green catch-zone WIDTH (track fraction): scales linearly with fishing level from
 *  `min` at level 1 up to `max` at level `maxLevel` (60) and stays there. Tunable in the debug panel. */
export const FISH_ZONE_W = { min: 0.10, max: 0.25, maxLevel: 60 };
/** Extra green-zone width while the FISHING HAT is worn (hat style 4). Added on top of fishZoneWidth. */
export const FISH_HAT_ZONE_BONUS = 0.02;
/** Fish move this much slower in the radar while the Fishing Hat is worn (0.9 = −10% speed). */
export const FISH_HAT_SPEED_MULT = 0.9;
/** The c3d hat style id that grants the fishing bonus, + its item id (the Fishing Hat equips to
 *  the COSMETIC hat slot — cosHat holds the item id — since hats aren't stat gear). */
export const FISHING_HAT_STYLE = 4;
export const FISHING_HAT_ID = "fishing_hat";
export function fishZoneWidth(level, min = FISH_ZONE_W.min, max = FISH_ZONE_W.max) {
    const t = Math.max(0, Math.min(1, (level - 1) / (FISH_ZONE_W.maxLevel - 1)));
    return min + t * (max - min);
}
/** Per-water-body current profile for the radar's green catch zone: `flow` = drift speed,
 *  `dir` = drift direction (-1 left, 0 none, +1 right), `chaos` = random jitter strength.
 *  The pond is near-still (no direction, a bit of randomness); the lake has a light breeze drift. */
export const WATER_PROFILES = {
    pond: { flow: 0.03, dir: 0, chaos: 0.30 },
    lake: { flow: 0.10, dir: 1, chaos: 0.12 },
};
export const DEFAULT_SKINNING = { level: 1, xp: 0 };
export const SKINNING_LEVEL_CAP = 100;
/** XP to advance skinning from `level`→`level+1` (60×L — same curve as the pickaxe). */
export function skinnerXpForLevel(level) { return xpToNextLevel(level); }
/** Designed tiers cover levels 1-50; 51-100 currently behaves as gold ("more color tiers later"). */
export const SKIN_TIERS = [
    { tier: "white", bandStart: 1, bandEnd: 10, unlock: 1, xp: 3 },
    { tier: "green", bandStart: 11, bandEnd: 20, unlock: 10, xp: 6 },
    { tier: "blue", bandStart: 21, bandEnd: 30, unlock: 20, xp: 9 },
    { tier: "purple", bandStart: 31, bandEnd: 40, unlock: 30, xp: 12 },
    { tier: "gold", bandStart: 41, bandEnd: 50, unlock: 40, xp: 15 },
];
export function skinTierInfo(tier) {
    return SKIN_TIERS.find((t) => t.tier === tier) ?? SKIN_TIERS[0];
}
/** The tier whose XP a player at `level` currently earns — the highest tier they've unlocked. */
export function currentSkinTier(level) {
    let cur = "white";
    for (const t of SKIN_TIERS)
        if (level >= t.unlock)
            cur = t.tier;
    return cur;
}
/** Can the player skin a mob of this tier? (You can't skin tiers above your level.) */
export function canSkinTier(tier, level) {
    return level >= skinTierInfo(tier).unlock;
}
/** XP is earned only from the player's CURRENT tier; lower tiers still give the hide but 0 XP. */
export function skinGainsXp(tier, level) {
    return tier === currentSkinTier(level);
}
export const SKIN_BASE_SECONDS = 15;
/** Skinning channel ms for `tier` at `level`: 15s base, −3s at the 5th & 10th level of the tier's
 *  band, floored at 9s (out-leveled tiers always skin at the 9s floor). */
export function skinDurationMs(tier, level) {
    const info = skinTierInfo(tier);
    let secs = SKIN_BASE_SECONDS;
    if (level >= info.bandStart + 4)
        secs -= 3;
    if (level >= info.bandStart + 9)
        secs -= 3;
    return Math.max(SKIN_BASE_SECONDS - 6, secs) * 1000;
}
export function skinXpFor(tier) { return skinTierInfo(tier).xp; }
/** Which tier a hog skins as (white = farmable from level 1). */
export const HOG_SKIN_TIER = "white";
/** Server px: how close to a corpse to skin it. */
export const SKIN_RANGE = 64;
export const DEFAULT_COOKING = { level: 1, xp: 0 };
export const COOKING_LEVEL_CAP = 10;
/** XP to advance cooking from `level`→`level+1` (60×L — same curve as the other skills). */
export function cookingXpForLevel(level) { return xpToNextLevel(level); }
// --- professions ------------------------------------------------------------
/** Crafting specializations taught by NPCs. Each player may hold up to MAX_PROFESSIONS, and each
 *  known profession has its own {level, xp} track (cap per profession — professionLevelCap). */
export const PROFESSIONS = ["leather_smith", "black_smith"];
export const MAX_PROFESSIONS = 2;
export const PROFESSION_NAMES = {
    leather_smith: "Leather Smith",
    black_smith: "Black Smith",
};
/** Hides handed over to learn Leather Smith / ore handed over to learn Black Smith. */
export const LEATHER_SMITH_HIDE_COST = 20;
export const BLACK_SMITH_ORE_COST = 20; // copper ore to learn Black Smith
export function isProfession(id) {
    return PROFESSIONS.includes(id);
}
export const PROFESSION_LEVEL_CAP = 10; // Leather Smith's tight ladder (the original cap)
/** Per-profession level caps. Leather Smith keeps the Lv10 ladder its recipes are tuned to;
 *  Black Smith scales like the MINING skill — cap 100, ten-level metal bands (copper 1-10,
 *  iron 10-20, silver 20-30, gold 30+ — see the smelting recipes). */
export const PROFESSION_LEVEL_CAPS = {
    leather_smith: PROFESSION_LEVEL_CAP,
    black_smith: 100,
};
export function professionLevelCap(id) {
    return isProfession(id) ? PROFESSION_LEVEL_CAPS[id] : PROFESSION_LEVEL_CAP;
}
/** Profession leveling slope (steeper than the skill curve so the cap-10 grind lands where we want).
 *  At 90×L the cumulative XP to Lv10 is 4,050 → ~200 crafts at 20 xp each; Lv5 (leather-suit unlock)
 *  is reached in ~45 crafts, so early progress is fast and the back half is the grind. */
export const PROFESSION_XP_SLOPE = 90;
/** XP to advance a profession from `level`→`level+1` (90×L). */
export function professionXpForLevel(level) {
    return PROFESSION_XP_SLOPE * Math.max(1, Math.floor(level));
}
/** A fresh profession starts at level 1. */
export function newProfessionEntry(id) { return { id, level: 1, xp: 0 }; }
/** Coerce a persisted professions blob to clean entries. Migrates the legacy `string[]` form
 *  (each id → a level-1 entry), drops unknown ids, clamps level/xp, de-dups, and caps to
 *  MAX_PROFESSIONS. Accepts strings or {id,level,xp}-ish objects (plain objects only — callers
 *  pass Mongoose subdocs through toObject first). */
export function normalizeProfessions(raw) {
    if (!Array.isArray(raw))
        return [];
    const out = [];
    const seen = new Set();
    for (const p of raw) {
        let id;
        let level = 1, xp = 0;
        if (typeof p === "string")
            id = p;
        else if (p && typeof p === "object") {
            const o = p;
            if (typeof o.id === "string")
                id = o.id;
            level = Math.floor(Number(o.level)) || 1;
            xp = Math.floor(Number(o.xp)) || 0;
        }
        if (!id || !isProfession(id) || seen.has(id))
            continue;
        seen.add(id);
        out.push({ id, level: Math.max(1, Math.min(professionLevelCap(id), level)), xp: Math.max(0, xp) });
        if (out.length >= MAX_PROFESSIONS)
            break;
    }
    return out;
}
/** Per-item gear bonuses. Leather armor: +5 armor + 1 to the rolled stat. */
export const GEAR_STATS = {
    leather_top_str: { armor: 5, stat: "strength", amount: 1 },
    leather_top_dex: { armor: 5, stat: "dex", amount: 1 },
    leather_top_agi: { armor: 5, stat: "agility", amount: 1 },
    leather_pants_str: { armor: 5, stat: "strength", amount: 1 },
    leather_pants_dex: { armor: 5, stat: "dex", amount: 1 },
    leather_pants_agi: { armor: 5, stat: "agility", amount: 1 },
    // Armored: same base (5 armor + 1 base stat). The +3 random points are per-instance `mods`.
    armored_leather_top_str: { armor: 5, stat: "strength", amount: 1 },
    armored_leather_top_dex: { armor: 5, stat: "dex", amount: 1 },
    armored_leather_top_agi: { armor: 5, stat: "agility", amount: 1 },
    armored_leather_pants_str: { armor: 5, stat: "strength", amount: 1 },
    armored_leather_pants_dex: { armor: 5, stat: "dex", amount: 1 },
    armored_leather_pants_agi: { armor: 5, stat: "agility", amount: 1 },
};
export function gearStat(itemId) { return GEAR_STATS[itemId]; }
/** Is this a STAT-bearing gear piece (leather / armored leather)? These go in the GEAR slots and drive
 *  armor + stat bonuses; every other garment is a COSMETIC (visual only, in the cosmetic slots). */
export function isStatGear(itemId) { return Object.prototype.hasOwnProperty.call(GEAR_STATS, itemId); }
/** Items that equip to their GEAR appearance slot (not the cosmetic slot) even without combat
 *  stats — combat gear PLUS functional equipment like the Fishing Hat (always shown; grants a
 *  gameplay bonus rather than armour). */
export function equipsToGearSlot(itemId) {
    return isStatGear(itemId) || itemId === FISHING_HAT_ID;
}
export function emptyEquipBonus() { return { armor: 0, strength: 0, dex: 0, knowledge: 0, agility: 0 }; }
/** Sum gear bonuses over a list of worn garment itemIds (nulls ignored). */
export function gearBonusOf(itemIds) {
    const b = emptyEquipBonus();
    for (const id of itemIds) {
        const g = id ? GEAR_STATS[id] : undefined;
        if (!g)
            continue;
        b.armor += g.armor;
        b[g.stat] += g.amount;
    }
    return b;
}
/** Reverse-map an appearance's stat-bearing slots (top + bottom) back to worn gear itemIds. */
export function wornGearItems(app) {
    return [equipToItem("top", app.top, app.topColor), equipToItem("bottom", app.bottom, app.bottomColor)]
        .filter((x) => x != null);
}
/** The equipment bonus implied by an appearance (its worn top + bottom). */
export function equipBonusFromAppearance(app) {
    return gearBonusOf(wornGearItems(app));
}
/** Total player armor = strength-derived armor (incl. any gear strength bonus) + flat gear armor. */
export function playerArmor(strengthLevel, eb) {
    return deriveArmor(strengthLevel + eb.strength) + eb.armor;
}
export const CRAFT_STATION_NAMES = {
    leather_machine: "Leather Processing Machine",
    crafting_table: "Crafting Table",
    forge: "Furnace", // the forge station IS a player-placed furnace furniture piece
};
/** Every craft channel runs a fixed 10-second progress bar. */
export const CRAFT_DURATION_MS = 10000;
/** Smelting XP tuning: a smelt pays SMELT_XP inside its metal's ten-level band, then drops to
 *  SMELT_XP_OUTLEVELED once the player levels past it (recipe.xpBandEnd) — see craftXp.
 *  Kept LOW on purpose (half the 90×L budget → a level costs exactly 9×L in-band smelts):
 *  smelting is the Black Smith's material grind, not the whole ladder — future ANVIL forging
 *  recipes (bars → gear) will carry the other half of the levelling XP. */
export const SMELT_XP = 10;
export const SMELT_XP_OUTLEVELED = 1;
/** All craft recipes across professions. Recipes only show in the craft menu for professions the
 *  player has learned. */
export const CRAFT_RECIPES = [
    {
        id: "leather_processing",
        profession: "leather_smith",
        name: "Leather Processing",
        station: "leather_machine",
        unlockLevel: 1,
        inputs: [{ itemId: HOG_HIDE_ID, qty: 2 }], // 2 white-tier (level 1-10) hides
        output: { itemId: "leather_low", qty: 1 }, // → 1 low quality (green) leather
        baseSuccess: 60,
    },
    {
        id: "leather_top",
        profession: "leather_smith",
        name: "Leather Top",
        station: "crafting_table",
        unlockLevel: 5,
        inputs: [{ itemId: "leather_low", qty: 3 }],
        output: { itemId: "leather_top_str", qty: 1 }, // representative (icon/name); stat is rolled
        rollVariants: ["leather_top_str", "leather_top_dex", "leather_top_agi"],
        // 50% at Lv5, +10%/level → 60/70/80/90 at Lv6-9, 99% at Lv10.
        baseSuccess: 50, successBaseLevel: 5, successStep: 10, successPerLevels: 1,
    },
    {
        id: "leather_pants",
        profession: "leather_smith",
        name: "Leather Pants",
        station: "crafting_table",
        unlockLevel: 5,
        inputs: [{ itemId: "leather_low", qty: 3 }],
        output: { itemId: "leather_pants_str", qty: 1 },
        rollVariants: ["leather_pants_str", "leather_pants_dex", "leather_pants_agi"],
        // 50% at Lv5, +10%/level → 99% at Lv10 (same as the leather top).
        baseSuccess: 50, successBaseLevel: 5, successStep: 10, successPerLevels: 1,
    },
    {
        id: "armored_leather_top",
        profession: "leather_smith",
        name: "Armored Leather Top",
        station: "crafting_table",
        unlockLevel: 8,
        // Consume a Leather Top (any variant, via upgrade) + 5 hides + 1 hog tusk.
        inputs: [{ itemId: HOG_HIDE_ID, qty: 5 }, { itemId: "hog_tusk", qty: 1 }],
        upgrade: { family: "leather_top" },
        output: { itemId: "armored_leather_top_str", qty: 1 }, // representative; base carries from the consumed piece
        rollMods: 3, // ARMORED_BONUS_POINTS
        baseSuccess: 80, successBaseLevel: 8, successStep: 10, successPerLevels: 1, // 80% L8 → 90% L9 → 99% L10
        xp: 40,
    },
    {
        id: "armored_leather_pants",
        profession: "leather_smith",
        name: "Armored Leather Pants",
        station: "crafting_table",
        unlockLevel: 8,
        inputs: [{ itemId: HOG_HIDE_ID, qty: 5 }, { itemId: "hog_tusk", qty: 1 }],
        upgrade: { family: "leather_pants" },
        output: { itemId: "armored_leather_pants_str", qty: 1 },
        rollMods: 3, // ARMORED_BONUS_POINTS
        baseSuccess: 80, successBaseLevel: 8, successStep: 10, successPerLevels: 1,
        xp: 40,
    },
    // — Black Smith SMELTING (station "forge" = a placed FURNACE furniture piece). The ladder IS
    // the mining tier ladder (Black Smith caps at 100 like mining — PROFESSION_LEVEL_CAPS): ten-
    // level metal bands, copper 1-10 · iron 10-20 · silver 20-30 · gold 30-40. Inside its band a
    // smelt pays SMELT_XP; once the player levels past the band (xpBandEnd) it drops to
    // SMELT_XP_OUTLEVELED (1) — mining's current-tier-only XP, so levelling means moving up to
    // the newest metal. Gold bands out too at 40: past it, every smelt is a 1-XP trickle.
    {
        id: "copper_smelting",
        profession: "black_smith",
        name: "Copper Smelting",
        station: "forge",
        unlockLevel: 1,
        inputs: [{ itemId: "ore_copper", qty: 2 }],
        output: { itemId: "copper_bar", qty: 1 },
        baseSuccess: 60,
        xp: SMELT_XP, xpBandEnd: 10,
    },
    {
        id: "iron_smelting",
        profession: "black_smith",
        name: "Iron Smelting",
        station: "forge",
        unlockLevel: 10,
        inputs: [{ itemId: "ore_iron", qty: 2 }],
        output: { itemId: "iron_bar", qty: 1 },
        // 60% at its unlock level, +10% every 2 levels (the copper curve, re-based per band).
        baseSuccess: 60, successBaseLevel: 10,
        xp: SMELT_XP, xpBandEnd: 20,
    },
    {
        id: "silver_smelting",
        profession: "black_smith",
        name: "Silver Smelting",
        station: "forge",
        unlockLevel: 20,
        inputs: [{ itemId: "ore_silver", qty: 2 }],
        output: { itemId: "silver_bar", qty: 1 },
        baseSuccess: 60, successBaseLevel: 20,
        xp: SMELT_XP, xpBandEnd: 30,
    },
    {
        id: "gold_smelting",
        profession: "black_smith",
        name: "Gold Smelting",
        station: "forge",
        unlockLevel: 30,
        inputs: [{ itemId: "ore_gold", qty: 2 }],
        output: { itemId: "gold_bar", qty: 1 },
        baseSuccess: 60, successBaseLevel: 30,
        xp: SMELT_XP, xpBandEnd: 40, // even gold bands out — past Lv40 every smelt trickles 1 XP
    },
];
export function recipeById(id) {
    return CRAFT_RECIPES.find((r) => r.id === id);
}
export function recipesForProfession(prof) {
    return CRAFT_RECIPES.filter((r) => r.profession === prof);
}
export function recipesForStation(station) {
    return CRAFT_RECIPES.filter((r) => r.station === station);
}
/** Success chance (%) for a recipe at a given profession level: `baseSuccess`% at `successBaseLevel`,
 *  then +`successStep`% every `successPerLevels` levels, capped at 99%. Defaults give the leather-
 *  processing curve (60% + 10% every 2 levels). Leather clothing overrides to 50% at Lv5 → 99% at Lv10. */
export function craftSuccessRate(recipe, professionLevel) {
    const baseLevel = recipe.successBaseLevel ?? 0;
    const per = recipe.successPerLevels ?? 2;
    const step = recipe.successStep ?? 10;
    const steps = Math.max(0, Math.floor((professionLevel - baseLevel) / per));
    return Math.min(99, recipe.baseSuccess + step * steps);
}
/** Base profession XP per successful craft (each recipe can override via `recipe.xp`). */
export const CRAFT_DEFAULT_XP = 20;
/** Profession XP earned per SUCCESSFUL craft — `recipe.xp` (default 20), 0 once the profession is
 *  capped (professionLevelCap — Leather Smith 10, Black Smith 100). Recipes with `xpBandEnd`
 *  (smelting) pay SMELT_XP_OUTLEVELED (1) once the player levels past their band — mining's
 *  current-tier-only XP, so levelling a Black Smith means moving up to the newest metal. */
export function craftXp(recipe, professionLevel) {
    if (professionLevel >= professionLevelCap(recipe.profession))
        return 0;
    if (recipe.xpBandEnd && professionLevel >= recipe.xpBandEnd)
        return SMELT_XP_OUTLEVELED;
    return recipe.xp ?? CRAFT_DEFAULT_XP;
}
/** Crafting stations in the city warehouse (server px) + how close to use one. */
export const LEATHER_MACHINE_ANCHOR = { x: 86 * 32, y: 98 * 32 };
export const CRAFTING_TABLE_ANCHOR = { x: 89 * 32, y: 100 * 32 };
export const CRAFT_STATION_RANGE = 70; // server px: stand this close to a station to use it
/** Where each FIXED station lives (server px). Stations absent here are PLAYER-PLACED furniture
 *  instead — see STATION_FURNITURE (the forge = any placed furnace). */
export const STATION_ANCHORS = {
    leather_machine: LEATHER_MACHINE_ANCHOR,
    crafting_table: CRAFTING_TABLE_ANCHOR,
};
/** Stations embodied by a placed FURNITURE piece: crafting requires standing within
 *  FURNITURE_USE_RANGE of a placed piece of this item id (same storey, for apartment homes). */
export const STATION_FURNITURE = {
    forge: "furnace",
};
export const COOK_RECIPES = [
    { id: "hog_burger", name: "Hog Burger", inputs: [{ itemId: "hog_meat", qty: 2 }], output: { itemId: "hog_burger", qty: 1 }, durationMs: 10000 },
    { id: "fries", name: "Fries", inputs: [{ itemId: "potato", qty: 1 }], output: { itemId: "fries", qty: 1 }, durationMs: 5000 },
];
export function cookRecipeById(id) {
    return COOK_RECIPES.find((r) => r.id === id);
}
// --- Threads sewing machine (employment-gated, NOT a learned profession) -----
/** A sewing-machine recipe — craft clothing from processed leather (`leather_low`). Available to anyone
 *  CLOCKED IN at Threads (owner + employees), no profession/level. Reuses the craft channel; always
 *  succeeds. Basic garments (tees / pants / shoes / circle tee) cost 2 leather; the Kapon Sporty sets
 *  (both colours) + the bomber jacket cost 10. */
const SEW_CHEAP = 2, SEW_PREMIUM = 10;
function sewRecipe(itemId, leather) {
    return { id: itemId, name: ITEM_NAMES[itemId], inputs: [{ itemId: "leather_low", qty: leather }], output: { itemId, qty: 1 }, durationMs: leather >= SEW_PREMIUM ? 12000 : 6000 };
}
export const SEW_RECIPES = [
    // Basic garments — 2 processed leather each.
    ...["white_tee", "red_tee", "green_tee", "blue_tee", "purple_tee", "black_shirt", "circle_shirt", "blue_jeans", "work_pants", "black_shoes"].map((id) => sewRecipe(id, SEW_CHEAP)),
    // Colour caps — 2 processed leather each.
    ...["black_cap", "white_cap", "red_cap", "green_cap", "blue_cap", "purple_cap"].map((id) => sewRecipe(id, SEW_CHEAP)),
    // Premium — the Kapon Sporty sets (both colours) + the bomber jacket — 10 processed leather each.
    ...["kapon_sweatshirt", "kapon_sweatpants", "purple_sweatshirt", "purple_sweatpants", "bomber_jacket"].map((id) => sewRecipe(id, SEW_PREMIUM)),
];
export function sewRecipeById(id) {
    return SEW_RECIPES.find((r) => r.id === id);
}
// --- restaurants (jobs + customers, one per building) -----------------------
/** Every restaurant building is the SAME Watabaga interior layout placed at a different centre (server
 *  px). So every fixture (pot, dispenser, fridge, vendor spot, customer route) DERIVES by a fixed offset
 *  from the centre — adding a restaurant is just a new centre here. Interiors are 13 wide × 10 deep. */
export const RESTAURANT_CENTERS = {
    food: { x: Math.round(270 * 32), y: Math.round(70 * 32) }, // Watabaga
    ghosts: { x: Math.round(310 * 32), y: Math.round(70 * 32) }, // Ghost's (clone, east of Watabaga's interior)
};
export const RESTAURANT_IDS = Object.keys(RESTAURANT_CENTERS);
export function isRestaurantBuilding(id) { return id in RESTAURANT_CENTERS; }
/** Business buildings run the full store loop (back-room chest + NPC resale + hireable staff on an
 *  hourly wage + register + walk-in customers): the two restaurants plus the Threads clothing store.
 *  Restaurants additionally cook + dispense (isRestaurantBuilding); Threads only stocks + sells. */
export const BUSINESS_IDS = [...RESTAURANT_IDS, "threads"];
export function isBusinessBuilding(id) { return BUSINESS_IDS.includes(id); }
/** THREADS interior — the generic 10×7 store box at THR_CX/THR_CZ in streetScene (NOT the restaurant
 *  kitchen layout), so its chest + customer route derive from this centre + depth. */
const THREADS_CENTER = { x: 110, y: 70 }; // interior tiles (mirror streetScene THR_CX/THR_CZ)
const THREADS_STORE_D = 11; // interior depth (tiles) — must match the buildStoreInterior `d` for Threads
function thrPx(xTiles, yTiles) { return { x: Math.round(xTiles * 32), y: Math.round(yTiles * 32) }; }
/** The back-room store CHEST anchor (server px) for a business building, or null (restaurants stock
 *  via the kitchen fridge — fridgeAnchor). For Threads: the back-LEFT corner, against the north wall. */
export function storeChestAnchor(bid) {
    if (bid !== "threads")
        return null;
    return thrPx(THREADS_CENTER.x - 5.5, THREADS_CENTER.y - THREADS_STORE_D / 2 + 1);
}
/** The THREADS sewing-machine crafting station anchor (server px), or null. Clocked-in staff stand here
 *  to craft clothing from processed leather. Back-right of the room (mirrors the prop in decorateThreads). */
export function sewingMachineAnchor(bid) {
    if (bid !== "threads")
        return null;
    return thrPx(THREADS_CENTER.x + 5, THREADS_CENTER.y - THREADS_STORE_D / 2 + 2.5);
}
/** A business building's street-side (village) door — where a walk-in customer enters. Restaurants
 *  use RESTAURANT_STREET_DOORS; Threads mirrors villageScene's threadsDoor (cx−9, front + 0.7). */
export function businessStreetDoor(bid) {
    if (bid === "threads")
        return thrPx(61, 64.2);
    return restaurantStreetDoor(bid);
}
/** The waypoint a walk-in customer paths to before stepping through a business's street door. */
export function businessEntryNode(bid) {
    return bid === "threads" ? 5 : restaurantEntryNode(bid); // node 5 = "Threads door approach"
}
/** Fixture offsets (world units) from a restaurant's interior centre. northZ = cz − 5, southZ = cz + 5. */
const FIX = {
    pot: { x: -4.5, y: -4.4 }, // left of the kitchen wall
    dispenser: { x: 2.5, y: -4.45 }, // right of the vendor
    fridge: { x: 4.5, y: -4.45 }, // right end of the kitchen
    vendor: { x: 0, y: -4.2 }, // behind the counter (centre)
    spawn: { x: 0, y: 3.5 }, // customer appears just inside the south door (door is centred, x = 0)
    counter: { x: 0, y: -2.4 }, // customer walks straight up to the counter, centred on the vendor
};
function fixAt(bid, off) {
    const c = RESTAURANT_CENTERS[bid] ?? RESTAURANT_CENTERS.food;
    return { x: Math.round(c.x + off.x * 32), y: Math.round(c.y + off.y * 32) };
}
/** A restaurant's cooking pot anchor (server px). Stand within CRAFT_STATION_RANGE to cook. */
export function cookingPotAnchor(bid) { return fixAt(bid, FIX.pot); }
/** A restaurant's drink dispenser anchor (server px). */
export function drinkDispenserAnchor(bid) { return fixAt(bid, FIX.dispenser); }
/** A restaurant's fridge anchor (server px). */
export function fridgeAnchor(bid) { return fixAt(bid, FIX.fridge); }
/** A restaurant's vendor stand (server px) — where the counter NPC + the customer's gaze target sit. */
export function vendorAnchor(bid) { return fixAt(bid, FIX.vendor); }
/** A restaurant's walk-in customer route (server px): appear just inside the door, stroll to the counter. */
export function customerRoute(bid) {
    if (bid === "threads") {
        const c = THREADS_CENTER;
        // Arrive just inside the south door, then stroll up to the front of the counter (facing north).
        return { spawn: thrPx(c.x, c.y + THREADS_STORE_D / 2 - 1.1), counter: thrPx(c.x, c.y - THREADS_STORE_D / 2 + 2.5) };
    }
    return { spawn: fixAt(bid, FIX.spawn), counter: fixAt(bid, FIX.counter) };
}
/** The street-side (village zone) door of each restaurant, in server px — where a walk-in customer
 *  enters from. MUST mirror villageScene's foodDoor/ghostsDoor (VIL_CX/CZ = 70,70; Watabaga at (cx, cz+19),
 *  Ghost Lab on the south row at (cx+9, cz+9); door y = front − 0.7). A customer ambles here, then "steps
 *  through" the door (teleports to the interior `customerRoute(bid).spawn`, since interiors are detached
 *  zone-boxes), and reverses the trip to leave. */
export const RESTAURANT_STREET_DOORS = {
    food: { x: Math.round(93 * 32), y: Math.round(75.8 * 32) }, // Watabaga, south row east of Ghost Lab toward the gate (cx+23, cz+9)
    ghosts: { x: Math.round(79 * 32), y: Math.round(75.8 * 32) }, // Ghost Lab, south row by the main road (cx+9, cz+9)
};
export function restaurantStreetDoor(bid) {
    return RESTAURANT_STREET_DOORS[bid] ?? null;
}
/** Townsperson first names — every CityNpc gets one (deterministic from its look seed, so it's stable
 *  and identical on every client). Picked to read as a small medieval-ish town. */
export const NPC_NAMES = [
    "Mara", "Tomas", "Bert", "Senna", "Otto", "Vera", "Cole", "Greta", "Finn", "Ilsa",
    "Roan", "Petra", "Dorn", "Hilde", "Casper", "Noor", "Edda", "Wren", "Magnus", "Liesel",
    "Joras", "Anke", "Stefan", "Rilla", "Gunnar", "Marta", "Bram", "Sigi", "Tove", "Hugo",
];
export function npcName(seed) {
    return NPC_NAMES[Math.abs(seed | 0) % NPC_NAMES.length];
}
/** Drinks the dispenser can pour — a clocked-in worker selects one that's on the menu. */
export const DISPENSER_DRINKS = ["water", "soda"];
export function isDispensableDrink(id) { return DISPENSER_DRINKS.includes(id); }
/** A poured drink lands in the building fridge after a DISPENSE_MS pour; the worker's wage is its
 *  foodCost (paid from the register, like any food task). */
export const DISPENSE_MS = 5000;
export const CUSTOMER_SPEED = 70; // server px/s — a leisurely stroll
export const CUSTOMER_WAIT_MS = 3000; // pause at the counter while "ordering"
// --- synced ambient city crowds (server-simulated wandering CityNpc townsfolk) ----------------
/** How many ambient townsfolk roam the village. */
export const VILLAGE_CROWD_COUNT = 8;
export const NPC_WANDER_SPEED = 38; // px/s — an idle amble
export const NPC_WANDER_PAUSE_MS = 2500; // dwell at each spot before moving on (± randomised)
/**
 * Village pedestrian WAYPOINT GRAPH (server px). Townsfolk walk the open street lanes between these nodes
 * (server BFS picks a route), so they roam the WHOLE village without clipping buildings, and return from
 * shops along real lanes instead of a straight line. Coords are in the VIL_CX/CZ = 70,70 tile frame
 * (north row z≈61, south row z≈79, mine in the NE quarter x89–97 z44–54, Watabaga at (79,89)). Edges
 * only run along verified building-free corridors. Node indices are referenced by VILLAGE_EDGES +
 * restaurantEntryNode.
 */
const VILLAGE_WP_TILES = [
    [52, 70], // 0  west-central (main street, west end)
    [61, 70], // 1  central, Threads/Casino column
    [70, 70], // 2  centre, Winston's column
    [79, 70], // 3  central, Bank/Ghost Lab column
    [100, 70], // 4  east-central (main street, east end — the widened city, wall at 102)
    [61, 65], // 5  Threads door approach (north)
    [70, 65], // 6  Winston's door approach (north)
    [79, 65], // 7  Bank door approach (north)
    [61, 75], // 8  Casino door approach (south)
    [79, 75], // 9  Ghost Lab door approach (south)
    [52, 62], // 10 north-west corner
    [100, 62], // 11 north-east corner (east lane)
    [52, 78], // 12 south-west
    [100, 78], // 13 south-east (east lane)
    [52, 85], // 14 far south-west
    [100, 85], // 15 far south-east
    [93, 75], // 16 Watabaga door approach (south row, east of Ghost Lab)
];
export const VILLAGE_WAYPOINTS = VILLAGE_WP_TILES.map(([x, y]) => ({ x: Math.round(x * 32), y: Math.round(y * 32) }));
/** Undirected edges (node-index pairs) — each runs along a building-free street lane. */
const VILLAGE_EDGES = [
    [0, 1], [1, 2], [2, 3], [3, 4], // central E-W spine
    [1, 5], [2, 6], [3, 7], // north-row door spurs
    [1, 8], [3, 9], // south-row door spurs
    [0, 10], [0, 12], [12, 14], // west lane (N-S, around the building rows)
    [4, 11], [4, 13], [13, 15], // east lane (N-S, along the widened east side)
    [9, 16], [16, 4], // Watabaga spur (east along the south row, back up to the road)
];
/** Adjacency list, built once from the edge pairs. */
const VILLAGE_ADJ = (() => {
    const adj = VILLAGE_WAYPOINTS.map(() => []);
    for (const [a, b] of VILLAGE_EDGES) {
        adj[a].push(b);
        adj[b].push(a);
    }
    return adj;
})();
/** BFS route from waypoint `from` to `to` as a list of node indices to walk (excludes `from`, includes
 *  `to`); empty if same node or unreachable. */
export function villagePath(from, to) {
    if (from === to || from < 0 || to < 0)
        return [];
    const prev = new Array(VILLAGE_WAYPOINTS.length).fill(-1);
    const seen = new Array(VILLAGE_WAYPOINTS.length).fill(false);
    const queue = [from];
    seen[from] = true;
    for (let h = 0; h < queue.length; h++) {
        const n = queue[h];
        if (n === to)
            break;
        for (const m of VILLAGE_ADJ[n])
            if (!seen[m]) {
                seen[m] = true;
                prev[m] = n;
                queue.push(m);
            }
    }
    if (!seen[to])
        return [];
    const path = [];
    for (let n = to; n !== from; n = prev[n])
        path.unshift(n);
    return path;
}
/** The waypoint nearest a point (server px) — for snapping an NPC onto the graph. */
export function nearestWaypoint(x, y) {
    let best = 0, bestD = Infinity;
    for (let i = 0; i < VILLAGE_WAYPOINTS.length; i++) {
        const d = (VILLAGE_WAYPOINTS[i].x - x) ** 2 + (VILLAGE_WAYPOINTS[i].y - y) ** 2;
        if (d < bestD) {
            bestD = d;
            best = i;
        }
    }
    return best;
}
/** The waypoint a walk-in customer paths to before stepping through a restaurant's street door. */
export function restaurantEntryNode(bid) {
    return bid === "ghosts" ? 9 : 16; // Ghost Lab door approach / Watabaga frontage
}
export const BASE_STAT_KEYS = ["strength", "dex", "knowledge", "agility"];
export const DEFAULT_BASE_STATS = {
    strength: { level: 1, xp: 0 }, dex: { level: 1, xp: 0 },
    knowledge: { level: 1, xp: 0 }, agility: { level: 1, xp: 0 },
};
/** XP to advance a base stat from `level`→`level+1` (60×L — same curve as the skills/professions). */
export function statXpForLevel(level) { return xpToNextLevel(level); }
/** Every base stat levels from 1 up to this cap (100). */
export const STAT_SOFT_CAP = 100;
export const STAT_HARD_CAP = 100;
/** Combat level = strength + dex + knowledge levels (agility excluded). Base (all L1) = 3. */
export function combatLevel(b) {
    return b.strength.level + b.dex.level + b.knowledge.level;
}
/** Stop earning combat XP once you've OUT-LEVELLED the target by this many combat levels. */
export const COMBAT_XP_OUTLEVEL_GAP = 3;
/** A melee hit on an object of `targetLevel` trains str+dex ONLY while you haven't out-levelled it —
 *  no XP once your combat level reaches `targetLevel + COMBAT_XP_OUTLEVEL_GAP` or beyond (so once the
 *  player is much higher level than the mob, fighting it stops giving XP). */
export function hitGrantsStatXp(b, targetLevel) {
    return combatLevel(b) < targetLevel + COMBAT_XP_OUTLEVEL_GAP;
}
// --- combat stat XP per hit ---------------------------------------------------------------------
// XP scales with the TARGET's level (tougher = more) and TAPERS as you approach the out-level gap.
// Past the gap, `hitGrantsStatXp` cuts XP off entirely (see grantHitStatXp), so the floor/falloff
// below only shapes the gain WITHIN the grace window. When higher-level tiers exist, fighting them
// yields far more (base × their level). NOTE: the XP CURVE (statXpForLevel) is unchanged.
export const STAT_XP_PER_HIT = 2; // base stat XP per qualifying hit, × the target's level
export const OUTLEVEL_FALLOFF = 0.02; // XP fraction lost per combat-level you out-level the target (past the gap)
export const OUTLEVEL_MIN_MULT = 0.2; // ...never below 20%
export function statXpForHit(targetLevel, attackerCombatLevel) {
    const base = STAT_XP_PER_HIT * Math.max(1, targetLevel);
    const over = Math.max(0, attackerCombatLevel - (targetLevel + COMBAT_XP_OUTLEVEL_GAP));
    return Math.max(1, Math.round(base * Math.max(OUTLEVEL_MIN_MULT, 1 - over * OUTLEVEL_FALLOFF)));
}
/** Add `amount` xp to a stat track; level up while over threshold (stops at the soft cap). Returns a
 *  fresh track + how many levels were gained. */
export function addStatXp(track, amount) {
    let { level, xp } = track;
    if (level >= STAT_SOFT_CAP)
        return { track: { level, xp: 0 }, gained: 0 };
    xp += amount;
    let gained = 0;
    while (level < STAT_SOFT_CAP && xp >= statXpForLevel(level)) {
        xp -= statXpForLevel(level);
        level += 1;
        gained += 1;
    }
    if (level >= STAT_SOFT_CAP)
        xp = 0;
    return { track: { level, xp }, gained };
}
// --- derived stats (computed from base-stat LEVELS; never persisted) ---------
// WoW-style POWER scaling: a high-level character is *dramatically* stronger (≈4,100 HP / ≈520 dmg /
// 75% armor / ×2.5 crit at level 100) and steamrolls low-level content.
export const BASE_HEALTH = 100;
export const STR_HEALTH_PER = 40; // maxHealth = 100 + 40×strength  → L100 = 4,100
export const STR_DAMAGE_PER = 5; // attack damage = weaponBase + 5×strength → sword L100 = 520
export const STR_ARMOR_PER = 6; // armor VALUE = 6×strength (feeds the % reduction below)
export const ARMOR_K = 200; // reduction = armor/(armor+K)
export const DMG_REDUCTION_CAP = 0.75; // armor caps incoming-damage reduction at 75%
export const BASE_CRIT = 0.05;
export const DEX_CRIT_PER = 0.007; // +crit chance per dex level
export const CRIT_MAX = 0.75; // crit chance caps at 75% (≈ dex 100)
export const CRIT_MULT = 1.5; // base crit multiplier (legacy; see deriveCritMult for the scaling one)
export const CRIT_MULT_BASE = 1.5;
export const DEX_CRIT_MULT_PER = 0.01; // crit DAMAGE grows ×0.01 per dex level
export const CRIT_MULT_MAX = 2.5;
export const EVASION_PER = 0.003; // dodge chance per (dex+agility) level
export const EVASION_CAP = 0.40; // dodge caps at 40% — with the 75% armor cap, combined mitigation ≤ 85%
export const ABILITY_POWER_PER = 5; // future spell/ability scaling per knowledge level
export const MOVE_SPEED_PER = 0.004; // +0.4% move speed per agility level
export const MOVE_SPEED_CAP = 0.40; // capped at +40%
export const BASE_STAMINA = 100;
export function deriveMaxHealth(strengthLevel) {
    return BASE_HEALTH + strengthLevel * STR_HEALTH_PER; // 100 + 40×strength
}
export function deriveAttackDamage(weaponBase, strengthLevel) {
    return weaponBase + strengthLevel * STR_DAMAGE_PER; // weaponBase + 5×strength
}
export function deriveArmor(strengthLevel) {
    return strengthLevel * STR_ARMOR_PER; // 6×strength (an armor VALUE → % reduction via deriveDamageReduction)
}
/** Fraction of incoming damage an armor value negates: armor/(armor+K), capped at DMG_REDUCTION_CAP. */
export function deriveDamageReduction(armor) {
    if (armor <= 0)
        return 0;
    return Math.min(DMG_REDUCTION_CAP, armor / (armor + ARMOR_K));
}
/**
 * Damage actually dealt after armor: rawDamage × (1 − reduction), where reduction = armor/(armor+K)
 * capped at 75%. Floored at 1 so any hit chips something. (WoW-style % mitigation — replaces the old
 * raw/armor model so a fully-armored target is reduced but never immune.)
 */
export function mitigatedDamage(rawDamage, armor) {
    if (rawDamage <= 0)
        return Math.max(0, Math.round(rawDamage));
    if (armor <= 0)
        return Math.round(rawDamage);
    return Math.max(1, Math.round(rawDamage * (1 - deriveDamageReduction(armor))));
}
export function deriveCritChance(dexLevel) {
    return Math.min(CRIT_MAX, BASE_CRIT + dexLevel * DEX_CRIT_PER);
}
/** Crit DAMAGE multiplier — grows with dex (×1.5 → ×2.5), so crit builds scale at high level. */
export function deriveCritMult(dexLevel) {
    return Math.min(CRIT_MULT_MAX, CRIT_MULT_BASE + dexLevel * DEX_CRIT_MULT_PER);
}
/** Chance to fully DODGE an incoming attack (0 damage), from dex + agility. Capped at 40% so that
 *  with the 75% armor cap a maxed build's combined mitigation stays ≤ ~85% (never unkillable). */
export function deriveEvasion(dexLevel, agilityLevel) {
    return Math.min(EVASION_CAP, (dexLevel + agilityLevel) * EVASION_PER);
}
/** Reserved ability/spell power from knowledge (no consumer yet — future abilities read it). */
export function deriveAbilityPower(knowledgeLevel) {
    return 10 + knowledgeLevel * ABILITY_POWER_PER;
}
export function deriveMoveSpeedMult(agilityLevel) {
    return 1 + Math.min(MOVE_SPEED_CAP, agilityLevel * MOVE_SPEED_PER);
}
export function deriveMaxStamina(agilityLevel, strengthLevel) {
    return BASE_STAMINA * (1 + (agilityLevel + strengthLevel) * 0.01);
}
/** How many random bonus points an armored leather craft rolls. */
export const ARMORED_BONUS_POINTS = 3;
export function emptyItemMods() { return { strength: 0, dex: 0, agility: 0 }; }
/** Roll `points` (default 3) bonus points randomly distributed among strength/dex/agility. */
export function rollItemMods(points = ARMORED_BONUS_POINTS) {
    const m = emptyItemMods();
    const keys = ["strength", "dex", "agility"];
    for (let i = 0; i < Math.max(0, Math.floor(points)); i++)
        m[keys[Math.floor(Math.random() * keys.length)]] += 1;
    return m;
}
/** Coerce a persisted/untrusted mods blob to clean non-negative integers (null if absent/empty). */
export function sanitizeItemMods(raw) {
    if (!raw || typeof raw !== "object")
        return undefined;
    const r = raw;
    const n = (v) => Math.max(0, Math.min(99, Math.floor(Number(v) || 0)));
    const m = { strength: n(r.strength), dex: n(r.dex), agility: n(r.agility) };
    return m.strength + m.dex + m.agility > 0 ? m : undefined;
}
export function itemModsTotal(m) { return m.strength + m.dex + m.agility; }
/** Compact wire form for syncing the WORN top/bottom mods on the Player schema ("s,d,a"; "" = none). */
export function encodeItemMods(m) {
    if (!m || itemModsTotal(m) <= 0)
        return "";
    return `${m.strength},${m.dex},${m.agility}`;
}
/** Parse the compact wire form back to clean mods (undefined when empty/all-zero). */
export function decodeItemMods(s) {
    if (!s)
        return undefined;
    const [a, b, c] = s.split(",");
    return sanitizeItemMods({ strength: a, dex: b, agility: c });
}
export const PET_GROW_MIN = 0.8;
export const PET_GROW_MAX = 3.0;
export const PET_KILL_STR_XP = 20; // base strength XP per mob kill the pet took part in (× grow)
export const PET_KILL_DEX_XP = 10; // base dex XP per mob kill the pet took part in (× grow)
export const PET_MAX_LEVEL = 10; // hoglet hard cap — stops gaining XP at this level
export const PET_HP_FACTOR = 1.0; // pet maxHealth = deriveMaxHealth(str) × this (≈ a player of equal strength)
export const PET_SIZE_PER_LEVEL = 0.05; // pet sprite grows 5% per pet level
export const PET_ATTACK_COOLDOWN_MS = 1200; // ms between pet swings
export const PET_ATTACK_WEAPON_BASE = 6; // weapon-equivalent base damage, scaled by strength via deriveAttackDamage
/** Random grow score in [PET_GROW_MIN, PET_GROW_MAX], 2 decimals. */
export function rollGrowScore() {
    return Math.round((PET_GROW_MIN + Math.random() * (PET_GROW_MAX - PET_GROW_MIN)) * 100) / 100;
}
/** Fresh level-1 pet stats with the given grow score. */
export function makePetStats(grow) {
    return { grow, strength: { level: 1, xp: 0 }, dex: { level: 1, xp: 0 }, agility: { level: 1, xp: 0 } };
}
/** The pet's overall LEVEL (drives sprite size) = its three stat levels summed; base (all L1) = 1,
 *  clamped at PET_MAX_LEVEL. */
export function petLevel(p) {
    return Math.min(PET_MAX_LEVEL, p.strength.level + p.dex.level + p.agility.level - 2);
}
/** True once the hoglet has hit its level cap (no more XP is awarded past this). */
export function petAtMaxLevel(p) {
    return p.strength.level + p.dex.level + p.agility.level - 2 >= PET_MAX_LEVEL;
}
/** Pet max health — scales off strength, beefier than a player of the same strength. */
export function petMaxHealth(p) {
    return Math.round(deriveMaxHealth(p.strength.level) * PET_HP_FACTOR);
}
/** Remove `amount` XP from a stat track (clamped at 0 within the current level — never de-levels). */
export function removeStatXp(track, amount) {
    return { level: track.level, xp: Math.max(0, track.xp - amount) };
}
/** Level of an attackable object — used against `hitGrantsStatXp` to decide if a hit trains. */
export const DUMMY_LEVEL = 1;
export const HOG_LEVEL = 5;
export const GOBLIN_LEVEL = 10;
export const SLIME_LEVEL = 15;
/** Spending this much stamina (cumulative — sprint + attack + block) grants +1 agility xp. */
export const AGILITY_STAMINA_COST = 100;
// --- stamina (CLIENT runtime resource; not a base stat, not persisted) -------
export const STAMINA_RUN_DRAIN = 18; // per second while sprinting
export const STAMINA_ATTACK_COST = 12; // per swing
export const STAMINA_BLOCK_COST = 10; // per block raise
export const STAMINA_JUMP_COST = 14; // per jump (Space) — drains the stamina bar, blocked when too low
export const STAMINA_REGEN_DELAY_MS = 3000; // no-consume delay before regen begins
export const STAMINA_REGEN_RATE = 22; // per second
/** Jump arc (client visual, shared so remotes hop with the same timing). The billboard rises on a
 *  parabola to JUMP_HEIGHT world units over JUMP_DURATION_MS, showing the static airborne pose. */
export const JUMP_DURATION_MS = 560;
export const JUMP_HEIGHT = 0.50;
export const STAMINA_RUN_RESUME = 10; // need at least this much to (re)start sprinting
export const DEFAULT_STATS = { health: 100, maxHealth: 100, hunger: 100, thirst: 100 };
/** Survival vitals: hunger + thirst each drop VITALS_DECAY_AMOUNT every VITALS_DECAY_INTERVAL_MS. */
export const VITALS_MAX = 100;
export const VITALS_DECAY_INTERVAL_MS = 120000; // 2 minutes
export const VITALS_DECAY_AMOUNT = 1;
/**
 * Blackjack table. A single fixed table sits in the town; players walk within
 * TABLE_RANGE of its anchor and press E to claim one of NUM_SEATS chairs. Seats are
 * indexed 0..NUM_SEATS-1 (the keys of the synced `seats` map).
 */
export const NUM_SEATS = 5;
export const SEAT_IDS = [0, 1, 2, 3, 4];
export function isValidSeatId(value) {
    return typeof value === "number" && SEAT_IDS.includes(value);
}
/** Sit-down detection radius in px — generous so talking to the casino host (who seats you)
 *  always counts as being "at the table" (≈8 tiles around the casino). */
export const TABLE_RANGE = 256;
/** The blackjack table lives inside the CASINO interior (zone "casino", world ≈ 150,69).
 *  Must match the table position in buildCasinoInterior (streetScene.ts). Server px. */
export const BLACKJACK_TABLE_ANCHOR = { x: 150 * 32, y: 69 * 32 };
/** ATM interaction radius in px (~2 tiles, easy to step up to). */
export const ATM_RANGE = 64;
/** Teleport portals are SHARED + timed: opening one (scroll / casino host) spawns a portal
 *  anyone nearby can step through until it collapses. Lifetime in seconds; capped count. */
export const PORTAL_LIFETIME_SEC = 60;
export const PORTAL_MAX = 16;
/** Portal destinations (server px) — must match the city/spawn layout. */
export const CITY_SPAWN = { x: 70 * 32, y: 76 * 32 }; // arrive in the city centre-south
/** Where a defeated player respawns after the downed timer (the city centre-south). */
export const COMBAT_RESPAWN = { x: 70 * 32, y: 70 * 32 }; // the middle of the city
export const SPAWN_ISLAND = { x: 10 * 32, y: 7 * 32 }; // in front of the desert tavern
/**
 * Blackjack rules + tuning. Bets are whole multiples of BET_STEP so the 3:2
 * blackjack payout is always an integer (1.5 × multiple-of-10 has no half-chip).
 */
export const MIN_BET = 10;
export const MAX_BET = 5000;
export const BET_STEP = 10;
/** Multi-deck shoe, freshly shuffled each round — a deal/hit can never exhaust it. */
export const SHOE_DECKS = 4;
/** Betting window before each round (ms). */
export const BETTING_WINDOW_MS = 15000;
/** Per-turn clock; on expiry the seat auto-stands (ms). */
export const TURN_TIMEOUT_MS = 15000;
/**
 * Grass cutting — a second city job. Grass tufts spawn around the city over time; with a
 * grass-cutter (granted by the Gardener), the player presses E next to one to cut it for cash.
 * It vanishes on cut. Spawn zones avoid buildings, roads, and the mine quarry.
 */
export const GRASS_VARIANTS = 3; // visual variants of a tuft
export const GRASS_MAX_NODES = 20; // most tufts alive at once
export const GRASS_SPAWN_INTERVAL_MS = 30000; // a new tuft every 30 seconds (up to the cap)
export const GRASS_RANGE = 40; // server px: how close to cut a tuft
export const GRASS_CUT_REWARD = 30; // cash per tuft cut
/** How many of the richest characters the bank's wealth rankboard shows. */
export const LEADERBOARD_SIZE = 10;
/** Player-to-player TRADING. A request can only be sent once per cooldown (anti-spam), and both
 *  players must be within range to start. The actual item/cash swap is atomic + re-validated. */
export const TRADE_REQUEST_COOLDOWN_MS = 30000; // 30s between outgoing trade requests
export const TRADE_RANGE = 220; // server px: how close to trade (~7 tiles)
/** In-world chat. Messages float as a pixel bubble above the speaker for a few seconds. */
export const CHAT_MAX_LEN = 100; // characters per message
export const CHAT_COOLDOWN_MS = 400; // min gap between a player's messages (anti-flood)
export const SERVER_NOTICE_MAX_LEN = 200; // characters per Ghost server-notification broadcast
export const CHAT_BUBBLE_MS = 6000; // how long a bubble stays up
/** Open-grass spawn rects in the city (server px), chosen to steer clear of buildings/roads/mine. */
// Open grass rects (server px) where tufts may spawn — the four corners of the (enlarged) city,
// in the open grass WEST and EAST of the two building rows and split around the central E–W
// road (z 67–73). All four clear the buildings, walls, gates, roads and mine, so a cut never
// lands you inside a structure.
export const GRASS_ZONES = [
    { x0: 39 * 32, y0: 42 * 32, x1: 44 * 32, y1: 66 * 32 }, // NW grass (the widened west strip, W of the tenement + Okka)
    { x0: 39 * 32, y0: 75 * 32, x1: 44 * 32, y1: 90 * 32 }, // SW grass (the widened west strip, W of Okka + the tackle shed)
    { x0: 84 * 32, y0: 58 * 32, x1: 98 * 32, y1: 65 * 32 }, // NE grass (E of Bank, S of the pond + quarry)
    { x0: 84 * 32, y0: 84 * 32, x1: 98 * 32, y1: 91 * 32 }, // SE grass (S of Watabaga, N of the working row)
];
/** Pause to show results AFTER the deal animation finishes, before the next betting window (ms). */
export const RESET_DELAY_MS = 4000;
/** How long the client takes to reveal each dealt card (ms). The SERVER pads the reset delay by
 *  this × the cards still being revealed, so the round never ends before the client has shown
 *  every card + the result (server resolves instantly; the client animates). */
export const BLACKJACK_DEAL_MS = 950;
/** Dealer peeks for blackjack on an Ace/10 up-card (no insurance is offered). */
export const DEALER_PEEK = true;
/** Card ranks and suits. A card is a rank+suit code, e.g. "AS", "TD", "9C". */
export const RANKS = ["2", "3", "4", "5", "6", "7", "8", "9", "T", "J", "Q", "K", "A"];
export const SUITS = ["S", "H", "D", "C"];
/**
 * Texas Hold'em poker table. A second fixed table in the CASINO interior (alongside blackjack).
 * Players walk within POKER_TABLE_RANGE of its anchor and press E to claim one of POKER_SEATS
 * chairs (keyed 0..POKER_SEATS-1 in the synced `seats` map), buy in from their cash wallet, and
 * play. Chips at the table are separate from the wallet and convert back on cash-out / leave.
 */
export const POKER_SEATS = 6;
export const POKER_SEAT_IDS = [0, 1, 2, 3, 4, 5];
export function isValidPokerSeatId(value) {
    return typeof value === "number" && POKER_SEAT_IDS.includes(value);
}
/** The poker table lives inside the CASINO interior (zone "casino"), east of the blackjack table.
 *  Must match the poker felt position in buildCasinoInterior (streetScene.ts). Server px. */
export const POKER_TABLE_ANCHOR = { x: 157 * 32, y: 70 * 32 };
/** Sit-down detection radius (px) — generous so talking to the poker dealer counts as "at the table". */
export const POKER_TABLE_RANGE = 256;
/** Client-side "press E to sit" prompt radius (px). */
export const POKER_SIT_RANGE = 150;
/** Blinds + buy-in bounds (whole chips). The big blind anchors the table stakes. */
export const SMALL_BLIND = 25;
export const BIG_BLIND = 50;
export const POKER_MIN_BUYIN = BIG_BLIND * 20; // 1,000
export const POKER_MAX_BUYIN = BIG_BLIND * 200; // 10,000
/** Per-turn action clock; on expiry the seat auto-checks (if free) or folds (ms). */
export const POKER_TURN_MS = 25000;
/** Pause between betting streets while the client flies in the community cards (ms). */
export const POKER_STREET_MS = 1200;
/** Pause to show the showdown result + winning hand before the next hand starts (ms). */
export const POKER_RESET_MS = 8000;
/** Open join/buy-in window before each hand is dealt (ms) — late players can sit in + buy chips. */
export const POKER_PREHAND_MS = 8000;
/** Pause after posting blinds + dealing hole cards before the first action (ms). */
export const POKER_BLINDS_MS = 1400;
/**
 * Initial shared-world ground items. Seeded into the room on (re)create; never
 * persisted. Positions are near the central spawn plaza (SPAWN = 320,240).
 */
/** No seeded ground drops in the spawn area — gear comes from the shops now. */
export const GROUND_ITEM_SEEDS = [];
/** Facing directions — 8-way since the c3d sheets carry all 8 yaw rows. */
export const Direction = {
    Down: "down",
    DownLeft: "down-left",
    Left: "left",
    UpLeft: "up-left",
    Up: "up",
    UpRight: "up-right",
    Right: "right",
    DownRight: "down-right",
};
//# sourceMappingURL=constants.js.map